import type { Translations } from './types';

export const de: Translations = {
  // Common
  'common.loading': 'Laden…',
  'common.prev': 'Zurück',
  'common.next': 'Weiter',
  'common.cancel': 'Abbrechen',
  'common.back': 'Zurück',
  'common.search': 'Suchen',
  'common.close': 'Schliessen',
  'common.exit': 'Verlassen',
  'common.noData': 'Keine Daten',
  'common.failedToLoad': 'Laden fehlgeschlagen',
  'common.retry': 'Erneut versuchen',
  'common.reloadPage': 'Seite neu laden',
  'common.beta': 'Beta',

  // Auth
  'auth.subtitle': 'Kart-Renn-Telemetrie-Plattform',
  'auth.signInDescription': 'Melde dich an, um auf die Telemetriedaten zuzugreifen.',
  'auth.signInWithGoogle': 'Mit Google anmelden',
  'auth.signInWithApple': 'Mit Apple anmelden',
  'auth.or': 'oder',
  'auth.signingIn': 'Anmeldung…',
  'auth.signOut': 'Abmelden',

  // OAuth Consent
  'oauth.authorizeTitle': 'Zugriff für {name} autorisieren',
  'oauth.authorizeSubtitle': '{name} möchte auf dein kart.win Telemetriekonto zugreifen.',
  'oauth.permissionsTitle': 'Angeforderte Berechtigungen',
  'oauth.permSessions': 'Alle Sessions, Runden und Telemetriekanäle lesen',
  'oauth.permAnalysis': 'Berechnete Metriken und Vergleiche lesen',
  'oauth.permSetups': 'Kart-Setups und Streckenkonfigurationen lesen',
  'oauth.signedIn': 'Angemeldet',
  'oauth.mustSignInFirst': 'Bitte melde dich an, um diese Anwendung zu autorisieren',
  'oauth.authorizeButton': 'Anwendung autorisieren',
  'oauth.missingParams': 'Erforderliche OAuth-Autorisierungsparameter fehlen',

  // Settings
  'settings.title': 'Einstellungen',
  'settings.account': 'Konto',
  'settings.profile': 'Profil',
  'settings.displayName': 'Anzeigename',
  'settings.saveName': 'Name speichern',
  'settings.nameSaved': 'Name erfolgreich aktualisiert',
  'settings.linkedAccounts': 'Verknüpfte Konten',
  'settings.linkGoogle': 'Google-Konto verknüpfen',
  'settings.linkApple': 'Apple-Konto verknüpfen',
  'settings.unlink': 'Trennen',
  'settings.linked': 'Verknüpft',
  'settings.notLinked': 'Nicht verknüpft',
  'settings.cannotUnlinkOnlyProvider': 'Mindestens ein Anmeldedienst muss verknüpft bleiben.',
  'settings.language': 'Sprache',
  'settings.userId': 'Benutzer-ID',
  'settings.signOut': 'Abmelden',
  'settings.connectedApps': 'Verbundene Anwendungen',
  'settings.connectedAppsDesc': 'Verwalte KI-Assistenten, IDEs und Tools, die über MCP auf deine Telemetriedaten zugreifen dürfen.',
  'settings.noConnectedApps': 'Noch keine verbundenen Anwendungen. Autorisiere einen MCP-Client (wie Claude Desktop oder Cursor), um ihn hier zu sehen.',
  'settings.authorizedOn': 'Autorisiert am',
  'settings.lastUsed': 'Zuletzt verwendet',
  'settings.neverUsed': 'Nie',
  'settings.revoke': 'Widerrufen',
  'settings.revokeAppConfirm': 'Möchtest du dieser Anwendung den Zugriff wirklich entziehen? Alle aktiven Verbindungen werden sofort ungültig.',
  'settings.appRevokedSuccess': 'Anwendungszugriff erfolgreich widerrufen',
  'settings.copyKey': 'Code kopieren',
  'settings.keyCopied': 'In Zwischenablage kopiert',
  'settings.mcpConfigSnippet': 'MCP-Client-Konfiguration',
  'settings.mcpConfigDesc': 'Füge diese Konfiguration zu deiner Claude Desktop (claude_desktop_config.json) oder Cursor MCP-Konfiguration hinzu. Die Autorisierung erfolgt automatisch über deinen Browser:',
  'settings.deleteAccount': 'Konto löschen',
  'settings.deleteAccountDesc': 'Lösche dein Konto sowie alle zugehörigen Sessions, Telemetriedaten, gespeicherten Analysen, Ausrüstung, Kart-Setups und Freigaben dauerhaft.',
  'settings.deleteAccountConfirmTitle': 'Konto dauerhaft löschen?',
  'settings.deleteAccountConfirmDesc': 'Diese Aktion kann nicht rückgängig gemacht werden. Alle deine eigenen Sessions, Telemetriedaten, gespeicherten Analysen, Ausrüstungsgegenstände, Kart-Setups, Teams und geteilten Session-Einträge werden sofort und unwiderruflich gelöscht.',
  'settings.deleteAccountConfirmPrompt': 'Gib zur Bestätigung „{phrase}“ in das Feld unten ein:',
  'settings.deleteAccountConfirmPhrase': 'mein konto löschen',
  'settings.deleteAccountConfirmButton': 'Mein Konto löschen',

  // Dashboard
  'dashboard.overview': 'Übersicht',
  'dashboard.subtitle': 'Telemetrie & Rundendaten',
  'dashboard.recentSessions': 'Letzte Sessions',
  'dashboard.total': '{n} gesamt',
  'dashboard.failedToLoadDashboard': 'Dashboard konnte nicht geladen werden',
  'dashboard.noDataAvailable': 'Keine Daten verfügbar',

  // Stats
  'stats.sessions': 'Sessions',
  'stats.laps': 'Runden',
  'stats.trackTime': 'Streckenzeit',
  'stats.tracks': 'Strecken',

  // Search
  'search.placeholder': 'Sessions suchen… (z.B. name:WOHLEN laps:>=5)',
  'search.button': 'Suchen',
  'search.syntaxHelp': 'Hilfe zur Abfragesyntax',
  'search.title': 'Abfragesyntax',
  'search.subtitle': 'Sessions mit Feld:Wert-Paaren filtern. Mehrere Begriffe werden mit AND verknüpft.',
  'search.closeDialog': 'Dialog schliessen',
  'search.operatorsTitle': 'Operatoren & Logik',
  'search.examplesTitle': 'Beispielabfragen',
  'search.numericComparisons': 'Numerische Vergleiche:',
  'search.range': 'Bereich:',
  'search.rangeDesc': '— findet Werte zwischen 5 und 20 (einschliesslich).',
  'search.booleanLogic': 'Boolesche Logik:',
  'search.booleanLogicDesc': 'Begriffe werden implizit mit',
  'search.quotes': 'Anführungszeichen:',
  'search.quotesDesc': 'Werte mit Leerzeichen in doppelte Anführungszeichen setzen, z.B.',

  // Filter UI
  'filter.add': 'Filter hinzufügen',
  'filter.allActive': 'Alle Filter aktiv',
  'filter.clearAll': 'Alle zurücksetzen',
  'filter.clear': 'Leeren',
  'filter.apply': 'Anwenden',
  'filter.cancel': 'Abbrechen',
  'filter.condition': 'Bedingung',
  'filter.presets': 'Vorlagen',
  'filter.customRange': 'Benutzerdefinierter Bereich',
  'filter.from': 'Von',
  'filter.to': 'Bis',
  'filter.min': 'Min',
  'filter.max': 'Max',
  'filter.value': 'Wert',
  'filter.searchOptions': 'Optionen suchen...',
  'filter.noOptions': 'Keine Optionen gefunden',
  'filter.loadingOptions': 'Optionen werden geladen...',

  // Search fields
  'search.field.bareText': '(Freitext)',
  'search.field.bareTextDesc': 'Findet Strecken- oder Fahrernamen, die den Text enthalten.',
  'search.field.name': 'name',
  'search.field.nameDesc': 'Findet Strecken- oder Fahrernamen, die den Wert enthalten.',
  'search.field.driver': 'driver',
  'search.field.driverDesc': 'Findet Fahrernamen, die den Wert enthalten.',
  'search.field.laps': 'laps',
  'search.field.lapsDesc': 'Nach Rundenanzahl filtern. Unterstützt =, >, >=, <, <= und Bereiche.',
  'search.field.temp': 'temp',
  'search.field.tempDesc': 'Nach Temperatur (°C) filtern. Unterstützt Vergleichsoperatoren und Bereiche.',
  'search.field.season': 'season',
  'search.field.seasonDesc': 'Filtert nach Saisonjahr (aus dem Sessiondatum). Unterstützt Vergleichsoperatoren.',
  'search.field.from': 'from',
  'search.field.fromDesc': 'Sessions ab einem Datum. Akzeptiert YYYY-MM-DD oder relativ: -7d, -2w, -3m, -1y.',
  'search.field.to': 'to',
  'search.field.toDesc': 'Sessions bis zu einem Datum. Gleiche Formate wie „from".',

  // Search examples
  'search.example.wohlenLaps': 'Sessions in Wohlen mit 5+ Runden',
  'search.example.yanick30d': 'Yanicks Sessions der letzten 30 Tage',
  'search.example.tempLaps': 'Sessions bei 15–25 °C mit 3+ Runden',
  'search.example.wohlenMagny': 'Wohlen- oder Magny-Sessions des letzten Jahres',

  // Session list
  'sessionList.date': 'Datum',
  'sessionList.time': 'Zeit',
  'sessionList.track': 'Strecke',
  'sessionList.sessions': 'Sessions',
  'sessionList.driver': 'Fahrer',
  'sessionList.laps': 'Runden',
  'sessionList.best': 'Beste',
  'sessionList.weather': 'Wetter',
  'sessionList.lapsUnit': '{n} Runden',
  'sessionList.sessionCount': '{count} Sessions',
  'sessionList.sessionCountSingular': '1 Session',
  'sessionList.noSessions': 'Keine Sessions gefunden. Versuche eine andere Suche.',

  // Track bests
  'trackBests.title': 'Streckenrekorde',

  // Session detail
  'session.title': 'Session',
  'session.failedToLoad': 'Session konnte nicht geladen werden',
  'session.noData': 'Keine Session-Daten',
  'session.chipOverview': 'Übersicht',
  'session.chipDiff': 'Diff',
  'session.tabWeather': 'Wetter',
  'session.tabLaps': 'Runden',
  'session.tabSetup': 'Setup',
  'session.tabManage': 'Verwalten',
  'session.dangerZone': 'Gefahrenbereich',
  'session.deleteDescription': 'Diese Session, alle aufgezeichneten Runden und Telemetriedaten dauerhaft löschen. Diese Aktion kann nicht rückgängig gemacht werden.',
  'session.delete': 'Session löschen',
  'session.deleteConfirmTitle': 'Session löschen',
  'session.deleteConfirmMessage': 'Möchtest du diese Session wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden und löscht alle Runden- und Telemetriedaten dauerhaft.',
  'session.deleteSuccess': 'Session erfolgreich gelöscht',
  'session.deleteFailed': 'Fehler beim Löschen der Session',
  'session.deleting': 'Session wird gelöscht...',

  // Session meta
  'sessionMeta.date': 'Datum',
  'sessionMeta.driver': 'Fahrer',
  'sessionMeta.track': 'Strecke',
  'sessionMeta.laps': 'Runden',
  'sessionMeta.device': 'Gerät',
  'sessionMeta.status': 'Status',
  'sessionMeta.tyres': 'Reifen',
  'sessionMeta.tyreAge': '{n} Runden',
  'sessionMeta.fresh': 'Frisch',

  // Weather
  'weather.wind': 'Wind',
  'weather.humidity': 'Luftfeuchtigkeit',
  'weather.cloudCover': 'Bewölkung',
  'weather.precipitation': 'Niederschlag',
  'weather.pressure': 'Luftdruck',
  'weather.feels': 'Gefühlt',
  'weather.clear': 'Klar',
  'weather.partlyCloudy': 'Teilweise bewölkt',
  'weather.overcast': 'Bedeckt',
  'weather.fog': 'Nebel',
  'weather.drizzle': 'Nieselregen',
  'weather.rain': 'Regen',
  'weather.freezingRain': 'Gefrierender Regen',
  'weather.snow': 'Schnee',
  'weather.rainShowers': 'Regenschauer',
  'weather.snowShowers': 'Schneeschauer',
  'weather.thunderstorm': 'Gewitter',
  'weather.unknown': 'Unbekannt',

  // Lap table
  'lapTable.title': 'Rundenzeiten',
  'lapTable.theoLap': 'Schnellste Runde',
  'lapTable.aggregation': 'Aggregation',
  'lapTable.compare': 'Vergleichen',
  'lapTable.lapTime': 'Rundenzeit',
  'lapTable.delta': 'Delta',
  'lapTable.maxRpm': 'Max RPM',
  'lapTable.minRpm': 'Min RPM',
  'lapTable.maxSpeed': 'Max Speed',
  'lapTable.minSpeed': 'Min Speed',
  'lapTable.tyreAge': 'Reifenalter',

  // Diff chart
  'diffChart.outlierLap': 'Irrelevant',
  'diffChart.deltaToBest': 'Delta zur Bestzeit',

  // Lap analysis
  'lapAnalysis.title': 'Runde {n} Analyse',
  'lapAnalysis.resetZoom': 'Zoom zurücksetzen',
  'lapAnalysis.speed': 'Speed',
  'lapAnalysis.acceleration': 'Beschleunigung',
  'lapAnalysis.rpm': 'RPM',
  'lapAnalysis.gForce': 'G-Force',
  'lapAnalysis.trackMap': 'Streckenkarte',
  'lapAnalysis.failedToLoad': 'Telemetriedaten konnten nicht geladen werden',
  'lapAnalysis.noTelemetry': 'Keine Telemetriedaten für diese Runde.',

  // Lap info
  'lapInfo.lapTime': 'Rundenzeit',
  'lapInfo.maxRpm': 'Max RPM',
  'lapInfo.minRpm': 'Min RPM',
  'lapInfo.maxSpeed': 'Max Speed',
  'lapInfo.maxTemp': 'Max Temp',
  'lapInfo.gpsOffset': 'GPS-Offset',
  'lapInfo.gpsOffsetTooltip': 'Korrigierte GPS-Drift: {east} O, {north} N',

  // Navigation
  'nav.data': 'Daten',
  'nav.home': 'Sessions',
  'nav.tools': 'Tools',
  'nav.compare': 'Vergleich',
  'nav.drivers': 'Fahrer',
  'nav.tracks': 'Strecken',
  'nav.settings': 'Einstellungen',

  // Analysis
  'analysis.title': 'Analyse-Tools',
  'analysis.subtitle': 'Erkunde deine Telemetriedaten mit speziellen Analyse-Tools.',
  'analysis.sector.title': 'Sektoranalyse',
  'analysis.sector.desc': 'Vergleiche Rundensektoren über mehrere Sessions hinweg, um die theoretisch beste Runde zu finden und die Konstanz zu analysieren.',


  // Braking Analysis
  'analysis.braking.title': 'Bremsanalyse',
  'analysis.braking.desc': 'Analysiere Bremspunkte, Verzögerungszonen, G-G-Reibkreise und Trail-Braking-Effizienz.',
  'braking.title': 'Bremsanalyse',
  'braking.desc': 'Analysiere Bremspunkte, Verzögerungszonen, G-G-Reibkreise und Trail-Braking-Effizienz.',
  'braking.wizardChooseTrack': 'Strecke wählen',
  'braking.wizardSelect': 'Sessions wählen',
  'braking.wizardResults': 'Ergebnisse',
  'braking.noTracksWithCorners': 'Keine Strecken mit konfigurierten Kurven verfügbar.',
  'braking.noSessionsForTrack': 'Keine Sessions gefunden für Strecke',
  'braking.noBrakingZones': 'Keine Bremszonen für diese Strecke gefunden.',
  'braking.selectZone': 'Bremszone wählen',
  'braking.zone': 'Bremszone',
  'braking.zones': 'Bremszonen',
  'braking.brakePoint': 'Bremspunkt',
  'braking.apex': 'Scheitelpunkt',
  'braking.exit': 'Ausgang',
  'braking.distance': 'Bremsweg',
  'braking.duration': 'Bremsdauer',
  'braking.initialSpeed': 'Anfangs-Speed',
  'braking.apexSpeed': 'Min.-Speed',
  'braking.minSpeed': 'Min.-Speed',
  'braking.brakeRelease': 'Bremslösen',
  'braking.exitSpeed': 'Ausgangs-Speed',
  'braking.speedDrop': 'Geschwindigkeitsabfall',
  'braking.peakDecel': 'Max. Verzögerung',
  'braking.peakLateral': 'Max. Querbeschleunigung',
  'braking.trailScore': 'Trail-Braking-Score',
  'braking.trailScoreDesc': 'Ausnutzung des Reibwerts beim Übergang von Längsverzögerung zu Querbeschleunigung.',
  'braking.scoreOptimal': 'Optimal',
  'braking.scoreGood': 'Gut',
  'braking.scoreNeedsWork': 'Ausbaufähig',
  'braking.sessionMedian': 'Session-Median',
  'braking.selectedLap': 'Ausgewählte Runde',
  'braking.speedTransition': 'Geschwindigkeitsverlauf',
  'braking.decelAndGrip': 'Verzögerung & Grip',
  'braking.trailScoreAdviceOptimal': 'Optimal: Hervorragender Bremskraftabbau am Kurveneingang bei hoher Ausnutzung des Reibwerts.',
  'braking.trailScoreAdviceGood': 'Gut: Gleichmäßiger Übergang. Der Bremsdruck kann noch etwas tiefer in die Kurve mitgeführt werden.',
  'braking.trailScoreAdviceNeedsWork': 'Ausbaufähig: Abruptes Lösen der Bremse vor dem Einlenken. Achte auf progressives Lösen beim Einlenken.',
  'braking.overallHeader': 'Gesamt-Bremsanalyse',
  'braking.overallHeaderDesc': 'Streckenweite Trail-Braking-Effizienz und Kurven-Konsistenz über alle ausgewählten Runden und Sessions.',
  'braking.perCornerHeader': 'Kurven-Detailansicht',
  'braking.perCornerHeaderDesc': 'Detaillierte Kinematik, G-G-Reibkreis und Telemetriekurven für die ausgewählte Bremszone.',
  'braking.overallScore': 'Gesamt-Trail-Braking-Score',
  'braking.overallScoreDesc': 'Durchschnittliche Ausnutzung des Reibwerts über alle Kurven und Runden',
  'braking.mostConsistentCorner': 'Konstanteste Kurve',
  'braking.leastConsistentCorner': 'Inkonstanteste Kurve',
  'braking.consistency': 'Konsistenz',
  'braking.lapTimeline': 'Rundenverlauf Score',
  'braking.sessionComparison': 'Session-Vergleich',
  'braking.selectCornerHint': 'Klicken zum Anzeigen der Kurve',
  'braking.stdDevLabel': 'StdAbw',
  'braking.ggDiagram': 'G-G-Diagramm',
  'braking.frictionCircle': 'Reibkreis',
  'braking.telemetryChart': 'Brems-Telemetrie',
  'braking.speed': 'Geschwindigkeit',
  'braking.gLong': 'Längsbeschleunigung G',
  'braking.gLat': 'Querbeschleunigung G',
  'braking.gTotal': 'Gesamt-G',
  'braking.allLaps': 'Runden',
  'braking.compareLap': 'Vergleichen',
  'braking.comparisonLap': 'Vergleich',
  'braking.clearComparison': 'Vergleich aufheben',
  'braking.lapTime': 'Rundenzeit',
  'braking.driver': 'Fahrer',
  'braking.lap': 'Runde',
  'braking.lapSpeedTable': 'Rundenübersicht Bremszonen',
  'braking.lapsAnalyzed': 'Runden analysiert',
  'braking.legendDecel': 'Geradeausverzögerung',
  'braking.legendTrail': 'Trail-Braking',
  'braking.legendApex': 'Scheitelpunkt',
  'braking.legendExit': 'Herausbeschleunigen',

  // Corner Speed Analysis
  'analysis.corner.title': 'Kurvenspeed-Analyse',
  'analysis.corner.desc': 'Messe und vergleiche Kurveneingangs-, Scheitelpunkt- und Ausgangsgeschwindigkeiten über Runden und Sessions hinweg.',
  'corner.title': 'Kurvenspeed-Analyse',
  'corner.wizardChooseTrack': 'Strecke wählen',
  'corner.wizardSelect': 'Sessions wählen',
  'corner.wizardResults': 'Ergebnisse',
  'corner.entrance': 'Eingang',
  'corner.apex': 'Scheitelpunkt',
  'corner.exit': 'Ausgang',
  'corner.entranceSpeed': 'Eingangs-Speed',
  'corner.apexSpeed': 'Scheitelpunkt-Speed',
  'corner.exitSpeed': 'Ausgangs-Speed',
  'corner.speedGain': 'Speed-Zuwachs',
  'corner.turn': 'Kurve',
  'corner.leftTurn': 'Linkskurve',
  'corner.rightTurn': 'Rechtskurve',
  'corner.avgSpeed': 'Durchschnitt',
  'corner.maxSpeed': 'Max. Speed',
  'corner.minSpeed': 'Min. Speed',
  'corner.fastest': 'Schnellste',
  'corner.slowest': 'Langsamste',
  'corner.spread': 'Streuung',
  'corner.deltaToBest': 'Δ zu Bester',
  'corner.deltaToAvg': 'Δ zu Schnitt',
  'corner.allCornersOverview': 'Alle Kurven in der Übersicht',
  'corner.aggregatedData': 'Aggregierte Geschwindigkeitsdaten',
  'corner.lapSpeedTable': 'Runden-Geschwindigkeitstabelle',
  'corner.lapsAnalyzed': 'Runden analysiert',
  'corner.noTracksWithCorners': 'Keine Strecken mit konfiguriertem Streckenverlauf und Kurven verfügbar.',
  'corner.noSessionsForTrack': 'Keine Sessions gefunden für Strecke',
  'corner.noCornersFound': 'Keine Kurven-Annotationen für diese Strecke gefunden.',
  'corner.selectCorner': 'Kurve wählen',
  'corner.rank': 'Rang',
  'corner.driver': 'Fahrer',
  'corner.lap': 'Runde',
  'corner.lapTime': 'Rundenzeit',
  'corner.session': 'Session',
  'corner.speed': 'Speed',
  'corner.avgMax': 'Ø / Max',
  'corner.activeMetric': 'Aktive Metrik:',
  'corner.metricSpeed': 'Geschwindigkeit',
  'corner.metricRpm': 'Drehzahl',
  'corner.metricDuration': 'Dauer',
  'corner.entranceRpm': 'Eingangs-Drehzahl',
  'corner.apexRpm': 'Scheitelpunkt-Drehzahl',
  'corner.exitRpm': 'Ausgangs-Drehzahl',
  'corner.rpmGain': 'Drehzahl-Zuwachs',
  'corner.rpm': 'U/min',
  'corner.duration': 'Dauer',
  'corner.transitDuration': 'Kurvenzeit',
  'corner.bestDuration': 'Beste Zeit',
  'corner.avgDuration': 'Durchschnittszeit',
  'corner.worstDuration': 'Langsamste Zeit',
  'corner.deltaToBestDuration': 'Δ zur Bestzeit',
  'corner.fullTransit': 'Gesamtdurchfahrt',



  // Sector analysis
  'sector.wizardChooseTrack': 'Strecke wählen',
  'sector.wizardDefine': 'Sektoren definieren',
  'sector.wizardSelect': 'Sessions wählen',
  'sector.wizardResults': 'Ergebnisse',
  'sector.title': 'Sektoranalyse',
  'sector.defineSectors': 'Sektoren definieren',
  'sector.clickToAdd': 'Klicken zum Hinzufügen · Ziehen zum Verschieben (max. 3 Sektoren)',
  'sector.selectSessions': 'Sessions wählen →',
  'sector.analyzingLap': '⏳ Runde wird analysiert…',
  'sector.analyzeLap': '⚡ Runde analysieren',
  'sector.noResults': 'Keine Ergebnisse verfügbar',
  'sector.noResultsHint': 'Gehe zurück und passe die Sektoren oder Sessions an',
  'sector.trackSectors': 'Streckensektoren',
  'sector.sectorN': 'Sektor {n}',
  'sector.noSessionsForTrack': 'Keine Sessions für Strecke gefunden',
  'sector.failedToLoad': 'Sektoranalyse konnte nicht geladen werden',
  'sector.lapStats': 'Rundenstatistik',
  'sector.lapStatsDriver': 'Fahrer',
  'sector.lapStatsTotal': 'Gesamt',

  // Sector panel
  'sectorPanel.time': 'Zeit',
  'sectorPanel.topSpeed': 'Top Speed',
  'sectorPanel.minSpeed': 'Min Speed',
  'sectorPanel.maxRpm': 'Max RPM',
  'sectorPanel.minRpm': 'Min RPM',
  'sectorPanel.duration': 'Dauer',
  'sectorPanel.best': 'Beste',
  'sectorPanel.avg': 'Ø',
  'sectorPanel.median': 'Median',
  'sectorPanel.worst': 'Schlechteste',
  'sectorPanel.p10': 'P10',
  'sectorPanel.p5': 'P5',
  'sectorPanel.consistency': 'Konstanz',
  'sectorPanel.lapByLap': 'Runde-für-Runde Verlauf',
  'sectorPanel.sessionMedians': 'Session-Mediane',
  'sectorPanel.medianTopSpeed': 'Median Top Speed',
  'sectorPanel.medianMinSpeed': 'Median Min Speed',
  'sectorPanel.medianMaxRpm': 'Median Max RPM',
  'sectorPanel.medianMinRpm': 'Median Min RPM',
  'sectorPanel.medianTime': 'Medianzeit',
  'sectorPanel.session': 'Session',
  'sectorPanel.lap': 'Runde',
  'sectorPanel.maxSpeed': 'Max Speed',

  // Split points
  'splitPoints.clearAll': 'Alle löschen',

  // Session selector
  'sessionSelector.allDrivers': 'Alle Fahrer',
  'sessionSelector.allWeather': 'Jedes Wetter',
  'sessionSelector.dryOnly': '☀️ Nur trocken',
  'sessionSelector.wetOnly': '🌧️ Nur nass',
  'sessionSelector.selectAll': 'Alle auswählen',
  'sessionSelector.deselectAll': 'Alle abwählen',

  // Theoretical best
  'theoBest.title': 'Theoretisch beste Runde',
  'theoBest.type': 'Typ',
  'theoBest.time': 'Zeit',
  'theoBest.delta': 'Delta',
  'theoBest.theoretical': 'Theoretisch',
  'theoBest.actualBest': 'Tatsächlich Beste',

  // Lap selector
  'lapSelector.title': 'Runden wählen',
  'lapSelector.loadingLaps': 'Runden laden…',
  'lapSelector.noLaps': 'Keine Runden',
  'lapSelector.best': 'BESTE',

  // Navigation – races
  'nav.races': 'Rennen',

  // Tracks
  'tracks.title': 'Strecken',
  'tracks.subtitle': 'Alle Strecken & Bestenlisten',
  'tracks.noData': 'Keine Strecken verfügbar',
  'tracks.failedToLoad': 'Strecken konnten nicht geladen werden',
  'tracks.sessions': '{n} Sessions',
  'tracks.raceSessions': '{n} Rennsessions',
  'tracks.bestLap': 'Beste Runde',
  'tracks.rank': '#',
  'tracks.lapTime': 'Rundenzeit',
  'tracks.driver': 'Fahrer',
  'tracks.date': 'Datum',
  'tracks.sectors': 'Sektoren',
  'tracks.weather': 'Wetter',
  'tracks.measuredLapsTab': 'Gemessene Runden',
  'tracks.raceTab': 'Rennen',
  'tracks.leaderboard': 'Rangliste',
  'tracks.category': 'Kategorie',
  'tracks.season': 'Saison',
  'tracks.position': 'Pos.',
  'tracks.sessionType': 'Typ',
  'tracks.sourceCanonical': 'Strecke',
  'tracks.sourceSession': 'Nur Session',
  'tracks.sourceRaceEvent': 'Nur Rennen',
  'tracks.searchPlaceholder': 'Strecken suchen…',
  'tracks.source': 'Quelle',
  'tracks.searchHelp.title': 'Streckensuche-Syntax',
  'tracks.searchHelp.subtitle': 'Suche nach Streckennamen. Gross-/Kleinschreibung und Akzente werden ignoriert.',
  'tracks.searchHelp.nameField': '(Text)',
  'tracks.searchHelp.nameDesc': 'Findet Strecken, deren Name den eingegebenen Text enthält. Akzente werden ignoriert (z.B. „Geneve" findet „Genève").',
  'tracks.searchHelp.example1': 'Wohlen',
  'tracks.searchHelp.example1Desc': 'Alle Strecken mit „Wohlen" im Namen',
  'tracks.searchHelp.example2': 'franciacorta',
  'tracks.searchHelp.example2Desc': 'Suche nach „Franciacorta" (Gross-/Kleinschreibung egal)',
  'tracks.searchHelp.example3': 'Sarno',
  'tracks.searchHelp.example3Desc': 'Alle Strecken mit „Sarno" im Namen',

  // Track detail tabs
  'tracks.layoutTab': 'Layout',
  'tracks.noTrackLayout': 'Kein Streckenlayout verfügbar',
  'tracks.officialLapsTab': 'Offizielle Runden',
  'tracks.noRaceEvents': 'Keine Rennevents gefunden',
  'tracks.round': 'Runde',
  'tracks.championship': 'Meisterschaft',
  'tracks.dateStart': 'Datum',
  'tracks.country': 'Land',
  'tracks.sessionsCount': 'Sessions',
  'tracks.driversCount': 'Fahrer',
  // Track race events search
  'tracks.raceEvents.searchPlaceholder': 'Rennevents suchen…',
  'tracks.raceEvents.searchHelp.title': 'Rennevents-Suche',
  'tracks.raceEvents.searchHelp.subtitle': 'Filtere Rennevents nach Saison oder Meisterschaftsname',
  'tracks.raceEvents.searchHelp.seasonField': 'season:<Jahr>',
  'tracks.raceEvents.searchHelp.seasonDesc': 'Nach Saisonjahr filtern (exakte Übereinstimmung)',
  'tracks.raceEvents.searchHelp.championshipField': 'championship:<Name>',
  'tracks.raceEvents.searchHelp.championshipDesc': 'Nach Meisterschaftsname filtern (Gross-/Kleinschreibung egal)',
  'tracks.raceEvents.searchHelp.example1': 'season:2026',
  'tracks.raceEvents.searchHelp.example1Desc': 'Alle Events der Saison 2026',
  'tracks.raceEvents.searchHelp.example2': 'championship:RMC',
  'tracks.raceEvents.searchHelp.example2Desc': 'Alle Events von Meisterschaften mit „RMC"',

  // Track leaderboard search
  'tracks.leaderboard.searchPlaceholder': 'Runden suchen...',
  'tracks.leaderboard.searchHelp.title': 'Leaderboard-Suche',
  'tracks.leaderboard.searchHelp.subtitle': 'Verwenden Sie Filter, um Ergebnisse einzugrenzen',
  'tracks.leaderboard.searchHelp.driverField': 'driver:<Name>',
  'tracks.leaderboard.searchHelp.driverDesc': 'Nach Fahrernamen filtern',
  'tracks.leaderboard.searchHelp.seasonField': 'season:<Jahr>',
  'tracks.leaderboard.searchHelp.seasonDesc': 'Nach Saisonjahr filtern',
  'tracks.leaderboard.searchHelp.categoryField': 'category:<Name>',
  'tracks.leaderboard.searchHelp.categoryDesc': 'Nach Kategorie filtern (z.B. DD2, Junior)',
  'tracks.leaderboard.searchHelp.typeField': 'type:<Renntyp>',
  'tracks.leaderboard.searchHelp.typeDesc': 'Nach Renntyp filtern (qualifying, heat-1, final...)',
  'tracks.leaderboard.searchHelp.posField': 'pos<:<N>',
  'tracks.leaderboard.searchHelp.posDesc': 'Maximale Endposition (z.B. pos<:3 = Podium)',
  'tracks.leaderboard.searchHelp.tempMinField': 'temp>:<N>',
  'tracks.leaderboard.searchHelp.tempMinDesc': 'Mindesttemperatur in °C',
  'tracks.leaderboard.searchHelp.tempMaxField': 'temp<:<N>',
  'tracks.leaderboard.searchHelp.tempMaxDesc': 'Maximaltemperatur in °C',
  'tracks.leaderboard.searchHelp.fromField': 'from:<JJJJ-MM-TT>',
  'tracks.leaderboard.searchHelp.fromDesc': 'Startdatum-Filter',
  'tracks.leaderboard.searchHelp.toField': 'to:<JJJJ-MM-TT>',
  'tracks.leaderboard.searchHelp.toDesc': 'Enddatum-Filter',
  'tracks.leaderboard.searchHelp.example1': 'driver:gubser season:2026',
  'tracks.leaderboard.searchHelp.example1Desc': 'Fahrer in der Saison 2026 finden',
  'tracks.leaderboard.searchHelp.example2': 'category:dd2 type:final',
  'tracks.leaderboard.searchHelp.example2Desc': 'Nur DD2-Finals',
  'tracks.leaderboard.searchHelp.example3': 'pos<:3 from:2026-01-01',
  'tracks.leaderboard.searchHelp.example3Desc': 'Podiumsplätze in 2026',

  // Drivers
  'drivers.title': 'Fahrer',
  'drivers.driver': 'Fahrer',
  'drivers.events': 'Events',
  'drivers.noData': 'Keine Fahrer gefunden',
  'drivers.failedToLoad': 'Fahrer konnten nicht geladen werden',
  'drivers.searchPlaceholder': 'Nach Name suchen…',

  // Races
  'races.title': 'Rennergebnisse',
  'races.seasons': 'Saisons',
  'races.events': '{n} Events',
  'races.categories': '{n} Kategorien',
  'races.round': 'Runde {n}',
  'races.allCategories': 'Alle Kategorien',
  'races.noEvents': 'Keine Events gefunden',
  'races.failedToLoad': 'Renndaten konnten nicht geladen werden',
  'races.noData': 'Keine Renndaten verfügbar',
  'races.position': 'Pos',
  'races.kart': '#',
  'races.driver': 'Fahrer',
  'races.bestLap': 'Schnellste Runde',
  'races.totalTime': 'Gesamtzeit',
  'races.gap': 'Abstand',
  'races.status': 'Status',
  'races.grid': 'Grid',
  'races.lap': 'Runde',
  'races.laps': 'Runden',
  'races.penalty': 'Strafe',
  'races.lapProgression': 'Rundenverlauf',
  'races.results': 'Ergebnisse',
  'races.driverHistory': 'Fahrer-Historie',
  'races.raceStarts': 'Starts',
  'races.podiums': 'Podien',
  'races.dnfDsq': 'DNF / DSQ',
  'races.qualiPoles': 'Quali Poles',
  'races.track': 'Strecke',
  'races.session': 'Session',
  'races.year': 'Jahr',
  'races.category': 'Kategorie',
  'races.pole': 'Pole',
  'races.winner': 'Sieger',
  'races.finish': 'Ergebnis',
  'races.sessionType.qualifying': 'Qualifying',
  'races.sessionType.heat-1': 'Vorlauf 1',
  'races.sessionType.heat-2': 'Vorlauf 2',
  'races.sessionType.heat-3': 'Vorlauf 3',
  'races.sessionType.heat-ab': 'Vorlauf A-B',
  'races.sessionType.heat-bc': 'Vorlauf B-C',
  'races.sessionType.heat-ac': 'Vorlauf A-C',
  'races.sessionType.final': 'Finale',

  // Race overview
  'races.overview': 'Übersicht',

  // Driver race search
  'races.searchPlaceholder': 'Rennen suchen… z.B. Castelletto oder type:race',
  'races.querySyntaxHelp': 'Hilfe zur Suchsyntax',
  'races.sessionCount': '{count} Sessions',
  'races.sessionCountSingular': '1 Session',
  'races.eventsFound': '{n} Event(s) gefunden',

  // Session detail tabs (below results table)
  'races.sessionTabWeather': 'Wetter',
  'races.sessionTabPace': 'Pace',
  'common.comingSoon': 'Demnächst',

  // Driver pace
  'races.tabRaces': 'Rennen',
  'races.tabStats': 'Statistiken',
  'races.tabPace': 'Pace',
  'races.paceDescription': 'Schnellste Rundenzeit pro Phase',
  'races.paceNoData': 'Keine Pace-Daten für diese Kategorie verfügbar.',
  'races.paceEvents': '{n} Events',
  'races.paceQualifying': 'Beste Rundenzeit über alle Events',

  // Driver stats
  'races.statWins': 'Siege',
  'races.statAvgFinish': 'Ø Zielposition',
  'races.statSeasons': 'Saisons',
  'races.statTracks': 'Strecken',
  'races.statCategories': 'Kategorien',
  'races.statTotalLaps': 'Gesamtrunden',

  'driverSearchHelp.title': 'Rennsuchsyntax',
  'driverSearchHelp.subtitle': 'Filtere Rennergebnisse mit Feld:Wert-Paaren. Mehrere Begriffe werden mit UND verknüpft.',
  'driverSearchHelp.bareText': '(Freitext)',
  'driverSearchHelp.bareTextDesc': 'Sucht im Streckennamen oder Meisterschaftsnamen nach dem Text.',
  'driverSearchHelp.track': 'track',
  'driverSearchHelp.trackDesc': 'Sucht im Streckennamen nach dem Wert.',
  'driverSearchHelp.championship': 'championship',
  'driverSearchHelp.championshipDesc': 'Sucht im Meisterschaftsnamen nach dem Wert.',
  'driverSearchHelp.category': 'category',
  'driverSearchHelp.categoryDesc': 'Sucht im Kategorienamen nach dem Wert.',
  'driverSearchHelp.type': 'type',
  'driverSearchHelp.typeDesc': 'Sucht nach Sessiontyp oder -bezeichnung (z.B. race, qualifying, heat).',
  'driverSearchHelp.position': 'position',
  'driverSearchHelp.positionDesc': 'Filtert nach Zielposition. Unterstützt =, >, >=, <, <= und Bereiche.',
  'driverSearchHelp.laps': 'laps',
  'driverSearchHelp.lapsDesc': 'Filtert nach gefahrenen Runden. Unterstützt Vergleichsoperatoren und Bereiche.',
  'driverSearchHelp.season': 'season',
  'driverSearchHelp.seasonDesc': 'Filtert nach Meisterschafts-Saisonjahr. Unterstützt Vergleichsoperatoren.',
  'driverSearchHelp.from': 'from',
  'driverSearchHelp.fromDesc': 'Rennen ab einem Datum. Akzeptiert JJJJ-MM-TT oder relativ: -7d, -2w, -3m, -1y.',
  'driverSearchHelp.to': 'to',
  'driverSearchHelp.toDesc': 'Rennen bis zu einem Datum. Gleiche Formate wie „from".',
  'driverSearchHelp.operatorsTitle': 'Operatoren & Logik',
  'driverSearchHelp.numericComparisons': 'Numerische Vergleiche:',
  'driverSearchHelp.range': 'Bereich:',
  'driverSearchHelp.rangeDesc': '— trifft auf Werte zwischen 5 und 20 zu (inklusive).',
  'driverSearchHelp.booleanLogic': 'Boolesche Logik:',
  'driverSearchHelp.booleanLogicDesc': 'Begriffe werden implizit mit',
  'driverSearchHelp.quotes': 'Anführungszeichen:',
  'driverSearchHelp.quotesDesc': 'Werte mit Leerzeichen in doppelte Anführungszeichen setzen, z.B.',
  'driverSearchHelp.examplesTitle': 'Beispielabfragen',
  'driverSearchHelp.example1Desc': 'Rennergebnisse in Castelletto',
  'driverSearchHelp.example2Desc': 'RMC-Meisterschaftspodien aus dem letzten Jahr',
  'driverSearchHelp.example3Desc': 'Finalergebnisse in den Top 3',

  // Drivers List Search Help
  'driversListSearchHelp.title': 'Fahrersuche',
  'driversListSearchHelp.subtitle': 'Fahrer nach Name finden',
  'driversListSearchHelp.bareText': 'Schlüsselwörter',
  'driversListSearchHelp.bareTextDesc': 'Schlüsselwörter werden mit Vorname und Nachname abgeglichen. Mehrere Schlüsselwörter werden als UND-Bedingungen behandelt.',
  'driversListSearchHelp.from': 'von',
  'driversListSearchHelp.fromDesc': 'Fahrer, die ab diesem Datum aktiv waren. Akzeptiert YYYY-MM-DD oder relativ: -7d, -2w, -3m, -1y.',
  'driversListSearchHelp.to': 'bis',
  'driversListSearchHelp.toDesc': 'Fahrer, die bis zu diesem Datum aktiv waren. Gleiche Formate wie "von".',
  'driversListSearchHelp.season': 'Saison',
  'driversListSearchHelp.seasonDesc': 'Fahrer, die in einer bestimmten Meisterschaftssaison aktiv waren. Unterstützt Vergleichsoperatoren wie season:>2020.',
  'driversListSearchHelp.category': 'Kategorie',
  'driversListSearchHelp.categoryDesc': 'Fahrer, die in einer bestimmten Kategorie (z.B. Rotax Max) aktiv waren.',
  'driversListSearchHelp.example.yanick': 'Fahrer namens Yanick finden',
  'driversListSearchHelp.example.senn': 'Fahrer namens Senn finden',
  'driversListSearchHelp.example.from': 'Aktive Fahrer ab 2026',
  'driversListSearchHelp.example.season': 'Aktive Fahrer im Jahr 2025 oder neuer',
  'driversListSearchHelp.example.category': 'Aktive Fahrer in Rotax Max',
  'drivers.lastAppearance': 'Letzter Auftritt',

  // Tyre Analysis
  'analysis.tyre.title': 'Reifenanalyse',
  'analysis.tyre.desc': 'Reifenhistorie analysieren und realistische Rundenzeiten basierend auf aktuellen Bedingungen vorhersagen.',
  'tyre.selectTitle': 'Reifen auswählen',
  'tyre.selectHint': 'Zeigt deine zuletzt verwendeten Reifensätze. Du kannst auch einen Barcode scannen.',
  'tyre.scanBarcode': 'Scannen',
  'tyre.barcodeSearch': 'Nach Barcode oder Name suchen...',
  'tyre.fastestLaps': 'Schnellste Runden pro Strecke',
  'tyre.expectations': 'Realistische Erwartungen',
  'tyre.totalLaps': 'Gefahrene Runden total',
  'tyre.totalSessions': 'Sessions',
  'tyre.longestSession': 'Längste Session',
  'tyre.ageLaps': '{n} Runden',
  'tyre.noData': 'Keine Daten',
  'tyre.expectedMedian': 'Erwartet',
  'tyre.matchingLaps': 'Passende Runden',
  'tyre.noTyresFound': 'Keine Reifensätze gefunden.',
  'tyre.noFastestLaps': 'Noch keine Runden mit diesem Reifensatz aufgezeichnet.',
  'tyre.noExpectations': 'Keine Strecken mit GPS-Daten für Prognosen verfügbar.',
  'tyre.dry': 'Trocken',
  'tyre.wet': 'Nass',
  'tyre.pointCamera': 'Kamera auf den Barcode richten',
  'tyre.wizardSelect': 'Reifen wählen',
  'tyre.wizardResults': 'Ergebnisse',
  'tyre.back': 'Zurück',
  'tyre.exit': 'Beenden',
  'tyre.trackColumn': 'Strecke',
  'tyre.bestLapColumn': 'Beste Runde',
  'tyre.dateColumn': 'Datum',
  'tyre.ageColumn': 'Alter',
  'tyre.weatherColumn': 'Wetter',
  'tyre.tempColumn': 'Temp',
  'tyre.expected': 'Erwartet:',

  // Tyre Overview
  'analysis.tyreOverview.title': 'Reifenübersicht',
  'tyreOverview.summaryTotal': 'Total',
  'tyreOverview.summaryFresh': 'Frisch',
  'tyreOverview.summaryWorn': 'Gebraucht',
  'tyreOverview.summaryEndOfLife': 'Am Ende',
  'tyreOverview.endOfLife': '⚠ Am Ende',
  'tyreOverview.scanButton': 'Scannen',
  'tyreOverview.noTyres': 'Keine aktiven Reifen gefunden.',
  'tyreOverview.scanNotFound': 'Kein Reifen für den gescannten Barcode gefunden.',
  'tyreOverview.laps': 'Runden',
  'tyreOverview.colTyre': 'Reifen',
  'tyreOverview.colCompound': 'Mischung',
  'tyreOverview.colLaps': 'Runden',
  'tyreOverview.colLastUsed': 'Zuletzt verwendet',
  'tyreOverview.colStatus': 'Status',
  'tyreOverview.discardButton': 'Als entsorgt markieren',
  'tyreOverview.discardAction': 'Entsorgen',
  'tyreOverview.discardSuccess': 'Reifensatz als entsorgt markiert.',
  'tyreOverview.searchPlaceholder': 'Reifen durchsuchen (z.B. brand:Mojo compound:dry laps:<50)...',
  'tyreOverview.searchHelp.title': 'Reifenübersicht-Suche',
  'tyreOverview.searchHelp.subtitle': 'Filtere und finde aktive Reifensätze nach Marke, Mischung, Runden, Barcode oder Strecke.',
  'tyreOverview.searchHelp.bareText': 'Text',
  'tyreOverview.searchHelp.bareTextDesc': 'Findet Marke, Typ, Referenznummer, Barcode oder den zuletzt verwendeten Streckennamen.',
  'tyreOverview.searchHelp.brandDesc': 'Nach Reifenhersteller oder Marke filtern (z.B. Mojo, Vega).',
  'tyreOverview.searchHelp.typeDesc': 'Nach Reifenmodell oder Typ filtern (z.B. D2, White).',
  'tyreOverview.searchHelp.nrDesc': 'Nach Reifensatz-Referenznummer filtern.',
  'tyreOverview.searchHelp.compoundDesc': 'Nach Mischungstyp ("dry" oder "wet") filtern.',
  'tyreOverview.searchHelp.lapsDesc': 'Nach gefahrenen Gesamtrunden mit Vergleichsoperatoren (=, >, <, >=, <=) oder Bereichen (z.B. 0..20) filtern.',
  'tyreOverview.searchHelp.barcodeDesc': 'Nach individuellem Reifen-Barcode oder Seriennummer filtern.',
  'tyreOverview.searchHelp.trackDesc': 'Nach Streckennamen filtern, auf der der Reifensatz zuletzt verwendet wurde.',
  'tyreOverview.searchHelp.fromDesc': 'Reifen, die am oder nach dem Datum zuletzt verwendet wurden (YYYY-MM-DD oder relativ wie -30d).',
  'tyreOverview.searchHelp.toDesc': 'Reifen, die am oder vor dem Datum zuletzt verwendet wurden (YYYY-MM-DD oder relativ wie -1m).',
  'tyreOverview.searchHelp.example.mojoDry': 'Trockenreifen von Mojo',
  'tyreOverview.searchHelp.example.lapsEol': 'Reifen, die das Lebensdauerende erreicht haben (70+ Runden)',
  'tyreOverview.searchHelp.example.lapsRange': 'Neue oder wenig gebrauchte Reifen mit 0 bis 20 Runden',
  'tyreOverview.searchHelp.example.trackFrom': 'Zuletzt in Lonato verwendete Reifen seit 1. August 2026',
  'tyreOverview.searchHelp.example.vegaWet': 'Reifen von Vega oder Regenreifen',

  // Devices
  'device.alfano7': 'Alfano 7',
  'device.mychron5': 'MyChron 5',

  // Import
  'import.title': 'Sessions importieren',
  'import.uploadZip': 'Telemetriedateien hochladen',
  'import.uploadZipDesc': 'Lade Alfano 7 (ZIP) oder MyChron 5 (.xrk, .xrz, ZIP) Telemetriedateien von deinem Computer hoch',
  'import.dragDropZip': 'ZIP-, XRK- oder XRZ-Dateien hierher ziehen',
  'import.dropZipHere': 'Dateien hier ablegen',
  'import.orBrowse': 'oder',
  'import.browseFiles': 'Dateien durchsuchen',
  'import.filesSelected': '{n} Datei(en) ausgewählt',
  'import.upload': 'Hochladen',
  'import.uploading': 'Wird hochgeladen…',
  'import.uploadingPct': 'Wird hochgeladen… {n}%',
  'import.imported': 'Importiert',
  'import.skipped': 'Übersprungen',
  'import.errors': 'Fehler',
  'import.errorDetailsTitle': 'Fehlerdetails',
  'import.successMessage': 'Sessions erfolgreich importiert. Sie erscheinen in deinem Dashboard.',
  'import.noNewSessions': 'Keine neuen Sessions gefunden. Alle Dateien wurden bereits importiert.',
  'import.onlyZipAccepted': 'Nur ZIP-, XRK- und XRZ-Dateien werden akzeptiert',
  'import.onlySupportedAccepted': 'Nur ZIP-, XRK- und XRZ-Dateien werden akzeptiert',
  'import.uploadFailed': 'Upload fehlgeschlagen (HTTP {status})',
  'import.connectionLost': 'Verbindung verloren',
  'import.uploadAborted': 'Upload abgebrochen',
  'import.importFailed': 'Import fehlgeschlagen',
  'import.importedFromZip': '{n} Session(s) importiert',
  'import.zipImportFailed': 'Import fehlgeschlagen: {error}',
  'import.useCurrentSetup': 'Aktuelles Setup verwenden',
  'offline.title': 'Du bist offline',
  'offline.message': 'Du hast derzeit keine Internetverbindung. Bitte überprüfe deine Netzwerkverbindung und versuche es erneut.',
  'settings.uploadSessions': 'Sessions hochladen',
  'settings.uploadSessionsDesc': 'Lade Alfano 7 oder MyChron 5 Telemetriedateien hoch, um deine Session-Daten zu importieren.',
  // Sharing
  'sharing.title': 'Teilen',
  'sharing.searchPlaceholder': 'Benutzer nach Name oder E-Mail suchen...',
  'sharing.noUsersFound': 'Keine Benutzer gefunden',
  'sharing.shareAll': 'Alle Sitzungen teilen',
  'sharing.shareSelected': 'Nur ausgewählte teilen',
  'sharing.readOnly': 'Schreibgeschützt',
  'sharing.readWrite': 'Lesen & Schreiben',
  'sharing.share': 'Teilen',
  'sharing.myShares': 'Meine Freigaben (ausgehend)',
  'sharing.sharedWithMe': 'Mit mir geteilt (eingehend)',
  'sharing.revoke': 'Widerrufen',
  'sharing.leave': 'Verlassen',
  'sharing.permissions': 'Berechtigungen',
  'sharing.sharedAll': 'Alle Sitzungen',
  'sharing.sharedSelected': 'Ausgewählte Sitzungen',
  'sharing.searching': 'Suchen…',
  'sharing.owner': 'Besitzer: {name}',
  'sharing.selectSessions': 'Sitzungen auswählen',
  'sharing.noSessions': 'Keine Sitzungen verfügbar',
  'sharing.laps': 'Runden',
  'sharing.searchHint': 'Mindestens 2 Zeichen eingeben',
  'sharing.newShare': 'Neue Freigabe',
  'sharing.stepUsers': 'Schritt 1: Benutzer auswählen',
  'sharing.stepConfigure': 'Schritt 2: Freigabe konfigurieren',
  'sharing.stepPermissions': 'Schritt 3: Berechtigungen',
  'sharing.next': 'Weiter',
  'sharing.selectedUsers': 'Ausgewählte Benutzer',
  'sharing.selectedSessions': '{count} Sitzung(en) ausgewählt',
  'sharing.filterSessions': 'Sitzungen filtern...',
  'sharing.showSelected': 'Ausgewählte anzeigen',
  'sharing.noSharesYet': 'Noch keine Freigaben',
  'sharing.emptyDescription': 'Teile deine Sitzungen mit anderen Fahrern oder Mechanikern.',
  'sharing.stepScope': 'Schritt 1: Sitzungen auswählen',
  'sharing.stepConfirm': 'Schritt 2: Bestätigen',
  'sharing.shareWithTeam': 'Mit diesem Team teilen',

  // Session tabs

  // Setup (readonly view)
  'setup.noSetup': 'Keine Setup-Daten für diese Session verfügbar.',
  'setup.loadingSetup': 'Setup laden…',
  'setup.loadFailed': 'Setup-Daten konnten nicht geladen werden.',
  'common.none': 'Keine',
  'common.notSet': 'Nicht gesetzt',
  'nav.setupSections': 'Setup-Bereiche',
  // Pressure
  'pressure.tyre': 'Reifensatz',
  'pressure.outgoing': 'Ausgehend',
  'pressure.cold': 'kalt',
  'pressure.incoming': 'Eingehend',
  'pressure.hot': 'warm',
  'pressure.fl': 'VL',
  'pressure.fr': 'VR',
  'pressure.rl': 'HL',
  'pressure.rr': 'HR',
  // Front
  'front.caster': 'Nachlauf',
  'front.camber': 'Sturz',
  'front.trackWidth': 'Spurbreite',
  'front.trackWidthUnit': 'mm',
  'front.rideHeight': 'Bodenfreiheit',
  'front.frontHubs': 'Vordernaben',
  'front.ackermann': 'Ackermann',
  'front.inner': 'Innen',
  'front.outer': 'Aussen',
  'front.toeAlignment': 'Spur',
  'front.torsionBar': 'Stabilisator',
  'front.hard': 'Hart',
  'front.medium': 'Mittel',
  'front.soft': 'Weich',
  // Rear
  'rear.axle': 'Achse',
  'rear.rearWidth': 'Hintere Breite',
  'rear.rearHubs': 'Hinternaben',
  'rear.loosenedBumper': 'Hinterer Stossfänger gelöst',
  // Engine
  'engine.engine': 'Motor',
  'engine.sparkPlug': 'Zündkerze',
  'engine.gearRatio': 'Übersetzung',
  'engine.pinion': 'Ritzel',
  'engine.crown': 'Kettenrad',
  // Carburetor
  'carb.carburetor': 'Vergaser',
  'carb.mainJet': 'Hauptdüse',
  'carb.idleJet': 'Leerlaufdüse',
  'carb.needleClip': 'Nadelclip',
  'carb.airScrew': 'Luftschraube',
  'carb.floatHeight': 'Schwimmerhöhe',
  // Chassis
  'chassisSelect.chassis': 'Chassis',
  'chassisSelect.weight': 'Gewicht',
  // Notes
  'notes.notes': 'Notizen',
  'notes.placeholder': 'Setup-Notizen…',

  // Navigation – inventory
  'nav.inventory': 'Inventar',
  'nav.organization': 'Organisation',
  'nav.setup': 'Setup',

  // Materials — common
  'common.add': 'Hinzufügen',
  'common.save': 'Speichern',
  'common.create': 'Erstellen',
  'common.created': 'Erstellt',
  'common.edit': 'Bearbeiten',
  'common.delete': 'Löschen',
  'common.optional': '(optional)',
  'common.brand': 'Marke',
  'common.type': 'Typ',
  'materials.profile': 'Profil',
  'materials.selectProfile': 'Profil auswählen…',
  'common.nr': 'Nr#',
  'common.nrInfo': 'Laufnummer zur Identifikation dieses Eintrags.',
  'common.team': 'Team',
  'common.year': 'Jahr',
  'common.stiffness': 'Steifigkeit',
  'common.width': 'Breite',
  'common.position': 'Position',

  // Materials — CRUD
  'crud.requiredField': '{field} ist erforderlich',
  'crud.updated': '{entity} aktualisiert',
  'crud.created': '{entity} erstellt',
  'crud.deleted': '{entity} gelöscht',
  'crud.confirmDelete': '{entity} "{name}" löschen?\nDies kann nicht rückgängig gemacht werden.',
  'crud.emptyState': 'Noch keine {title}. Füge dein erstes {entity} hinzu.',
  'crud.editEntity': '{entity} bearbeiten',
  'crud.newEntity': 'Neues {entity}',

  // Materials — general
  'materials.readOnly': 'Nur Lesen',
  'materials.selectTeam': 'Wähle ein Team um Inventar anzuzeigen.',

  // Materials — tabs
  'materials.tab.engines': 'Motoren',
  'materials.tab.tyres': 'Reifen',
  'materials.tab.chassis': 'Chassis',
  'materials.tab.carburetors': 'Vergaser',
  'materials.tab.axles': 'Achsen',
  'materials.tab.hubs': 'Naben',
  'materials.tab.spark_plugs': 'Zündkerzen',

  // Materials — entities
  'materials.entity.engine': 'Motor',
  'materials.entity.tyre': 'Reifen',
  'materials.entity.chassis': 'Chassis',
  'materials.entity.carburetor': 'Vergaser',
  'materials.entity.axle': 'Achse',
  'materials.entity.hub': 'Nabe',
  'materials.entity.spark_plug': 'Zündkerze',

  // Materials — tyre-specific
  'materials.tyre.compound': 'Mischung',
  'materials.tyre.compound.dry': 'Trocken',
  'materials.tyre.compound.wet': 'Nass',
  'materials.tyre.discarded': 'Aussortiert',

  // Materials — tyre IDs
  'materials.tyreId.title': 'Reifen-IDs',
  'materials.tyreId.fl': 'VL',
  'materials.tyreId.fr': 'VR',
  'materials.tyreId.rl': 'HL',
  'materials.tyreId.rr': 'HR',
  'materials.tyreId.scan': 'Barcode scannen',
  'materials.tyreId.duplicateInSet': 'Doppelte Reifen-ID in diesem Satz',
  'materials.tyreId.duplicateInOtherSet': 'ID {id} wird bereits von {tyre} verwendet',
  'materials.tyreId.placeholder': 'Reifen-ID scannen oder eingeben',
  'materials.tyreId.crossSetInfo': 'Einige Reifen-IDs kollidieren mit anderen Reifensätzen. Drücke erneut Speichern zum Überschreiben.',

  // Materials — hub positions
  'materials.position.front': 'Vorne',
  'materials.position.rear': 'Hinten',
  // Setup editing
  'setup.edit': 'Setup bearbeiten',
  'setup.editTitle': 'Setup bearbeiten',
  'setup.save': 'Setup speichern',
  'setup.saving': 'Speichern…',
  'setup.saved': 'Setup gespeichert',
  'setup.saveFailed': 'Setup konnte nicht gespeichert werden',
  'setup.pageTitle': 'Aktuelles Setup',
  'setup.pageSubtitle': 'Basiskonfiguration für neu importierte Sessions',
  'setup.editCurrentTitle': 'Aktuelles Setup bearbeiten',
  'setup.noCurrentSetup': 'Noch kein Setup konfiguriert. Klicke auf „Setup bearbeiten", um zu beginnen.',
  // Propagation
  'propagation.title': 'Änderungen anwenden',
  'propagation.subtitle': 'Setup-Änderungen auf andere Sessions anwenden',
  'propagation.changes': 'Änderungen',
  'propagation.sessions': 'Sessions',
  'propagation.selectAll': 'Alle auswählen',
  'propagation.deselectAll': 'Alle abwählen',
  'propagation.apply': 'Anwenden',
  'propagation.applying': 'Anwenden…',
  'propagation.applied': 'Auf Sessions angewandt',
  'propagation.skip': 'Überspringen',
  'propagation.selectAtLeastOne': 'Mindestens eine Session auswählen',
  'propagation.noMatching': 'Keine passenden Sessions gefunden',
  // Propagation field labels
  'propagation.field.chassis': 'Chassis',
  'propagation.field.weight': 'Gewicht',
  'propagation.field.engine': 'Motor',
  'propagation.field.tyre': 'Reifen',
  'propagation.field.carburetor': 'Vergaser',
  'propagation.field.axle': 'Achse',
  'propagation.field.flOut': 'VL Aus',
  'propagation.field.frOut': 'VR Aus',
  'propagation.field.rlOut': 'HL Aus',
  'propagation.field.rrOut': 'HR Aus',
  'propagation.field.flIn': 'VL Ein',
  'propagation.field.frIn': 'VR Ein',
  'propagation.field.rlIn': 'HL Ein',
  'propagation.field.rrIn': 'HR Ein',
  'propagation.field.rearWidth': 'Hintere Breite',
  'propagation.field.frontHubs': 'Vordernaben',
  'propagation.field.rearHubs': 'Hinternaben',
  'propagation.field.rearBumper': 'Hinterer Stossfänger',
  'propagation.field.caster': 'Nachlauf',
  'propagation.field.camber': 'Sturz',
  'propagation.field.trackWidth': 'Spurbreite',
  'propagation.field.rideHeight': 'Bodenfreiheit',
  'propagation.field.ackermann': 'Ackermann',
  'propagation.field.toe': 'Spur',
  'propagation.field.torsionBar': 'Stabilisator',
  'propagation.field.frontSprocket': 'Ritzel',
  'propagation.field.rearSprocket': 'Kettenrad',
  'propagation.field.mainJet': 'Hauptdüse',
  'propagation.field.idleJet': 'Leerlaufdüse',
  'propagation.field.needleClip': 'Nadelclip',
  'propagation.field.airScrew': 'Luftschraube',
  'propagation.field.floatHeight': 'Schwimmerhöhe',
  'propagation.field.notes': 'Notizen',
  'propagation.field.sparkPlug': 'Zündkerze',
  // Common additions
  'common.yes': 'Ja',
  'common.no': 'Nein',
  'common.skip': 'Überspringen',
  'common.applying': 'Anwenden…',

  // Teams
  'nav.teams': 'Teams',
  'team.subtitle': 'Teams und Mitglieder verwalten',
  'team.create': 'Team erstellen',
  'team.name': 'Teamname',
  'team.members': 'Mitglieder',
  'team.role.read': 'Lesen',
  'team.role.write': 'Schreiben',
  'team.role.manage': 'Verwalten',
  'team.role.owner': 'Besitzer',
  'team.addMember': 'Mitglied hinzufügen',
  'team.removeMember': 'Mitglied entfernen',
  'team.leave': 'Team verlassen',
  'team.confirmLeave': 'Bist du sicher, dass du dieses Team verlassen möchtest?',
  'team.cannotLeaveOwner': 'Du kannst das Team nicht verlassen, da du der Besitzer bist.',
  'team.created': 'Team erstellt',
  'team.memberAdded': 'Mitglied hinzugefügt',
  'team.memberRemoved': 'Mitglied entfernt',
  'team.roleUpdated': 'Rolle aktualisiert',
  'team.renamed': 'Team umbenannt',
  'team.empty': 'Keine Teams gefunden. Erstelle eins, um zu beginnen.',
  'team.noWriteAccess': 'Du hast keine Schreibrechte für dieses Team.',
  'team.tab.members': 'Mitglieder',
  'team.tab.sharing': 'Freigaben',
  'team.members.searchPlaceholder': 'Mitglieder suchen...',
  'team.sharing.searchPlaceholder': 'Freigaben suchen...',
  'team.members.searchTitle': 'Mitglieder suchen',
  'team.members.searchSubtitle': 'Mitglieder nach Name, E-Mail oder Rolle filtern',
  'team.sharing.searchTitle': 'Freigaben suchen',
  'team.sharing.searchSubtitle': 'Freigaben nach Besitzer oder Typ filtern',
  'team.members.field.bareText': '(beliebiger Text)',
  'team.members.field.bareTextDesc': 'Durchsucht nach Name',
  'team.members.field.name': 'name:',
  'team.members.field.nameDesc': 'Nach Mitgliedername filtern',
  'team.members.field.role': 'role:',
  'team.members.field.roleDesc': 'Nach Rolle filtern (read, write, manage)',
  'team.sharing.field.bareText': '(beliebiger Text)',
  'team.sharing.field.bareTextDesc': 'Durchsucht Besitzername',
  'team.sharing.field.owner': 'owner:',
  'team.sharing.field.ownerDesc': 'Nach Besitzer filtern',
  'team.sharing.field.type': 'type:',
  'team.sharing.field.typeDesc': 'Nach Typ filtern (all, selected)',

  // Lap Diff Analysis
  'analysis.lapdiff.title': 'Runden-Vergleich',
  'analysis.lapdiff.desc': 'Zwei Runden überlagern und Unterschiede in Geschwindigkeit, Drehzahl und Beschleunigung hervorheben.',
  'lapdiff.selectHint': 'Wähle genau 2 Runden zum Vergleichen.',
  'lapdiff.compare': 'Vergleichen',
  'lapdiff.comparing': 'Vergleiche…',
  'lapdiff.viewLapA': 'Runde A anzeigen',
  'lapdiff.viewLapB': 'Runde B anzeigen',
  'lapdiff.tabTime': 'Zeit',
  'lapdiff.tabSpeed': 'Geschwindigkeit',
  'lapdiff.tabRpm': 'Drehzahl',
  'lapdiff.tabAcceleration': 'Beschleunigung',
  'lapdiff.timeChartTitle': 'Zeitverlust über Streckenposition',
  'lapdiff.timeGained': 'Zeit gewonnen',
  'lapdiff.timeLost': 'Zeit verloren',
  'lapdiff.cumulativeDelta': 'Kumulatives Δt',
  'lapdiff.segmentDelta': 'Segment Δt',
  'lapdiff.trackPosition': 'Streckenposition',
  'lapdiff.avgDiff': 'Ø Differenz',
  'lapdiff.maxDiff': 'Max Differenz',
  'lapdiff.noGpsData': 'Keine GPS-Daten für den Vergleich verfügbar.',
  'lapdiff.pctBetter': '% besser',
  'lapdiff.segmentTime': 'Abschnitt',
  'lapdiff.fullLap': 'Ganze Runde',
  'lapdiff.fasterBy': '{lap} schneller um {time}',
  'lapdiff.equalTime': 'Gleiche Zeit',
  'lapdiff.clearAnchors': 'Anker löschen',
  'lapdiff.clickToPlace': 'Auf die Strecke klicken um Anker zu setzen',
  'lapdiff.segmentRange': 'Segmente #{start} – #{end}',
  'lapdiff.segmentDetail': 'Segment #{index}',

  // Aggregation Analysis
  'analysis.aggregation.title': 'Aggregation',
  'analysis.aggregation.desc': 'Brems-, Beschleunigungs- und Schaltpunkte über Sessions hinweg auf der Streckenkarte aggregieren.',
  'aggregation.brakingPoints': 'Bremsen',
  'aggregation.accelerationPoints': 'Beschleunigung',
  'aggregation.upshiftPoints': 'Hochschalten',
  'aggregation.top10Title': 'Top 10 schnellste Runden',
  'aggregation.noData': 'Keine Datenpunkte erkannt',
  'aggregation.noDataHint': 'Versuche andere Sessions auszuwählen.',
  'aggregation.trackShapeRequired': 'Streckenform erforderlich',
  'aggregation.trackShapeRequiredHint': 'Eine offizielle Streckenform ist erforderlich, um Aggregations-Boxplots anzuzeigen. Bitte erstelle eine Streckenform im Editor.',
  'aggregation.sessionSelectionHint': 'Je mehr Sessions ausgewählt werden, desto höher ist die Qualität und Genauigkeit der aggregierten Zonen.',
  'aggregation.brakingSegment': 'Bremszone',
  'aggregation.accelerationSegment': 'Beschleunigungszone',
  'aggregation.upshiftSegment': 'Schaltzone',
  'aggregation.tooltipSamples': ({ count }) => `${count} Messungen`,
  'aggregation.tooltipMedian': 'Median',
  'aggregation.tooltipSpread': 'Streuung',
  'aggregation.gpsHeatmap': 'GPS-Heatmap',
  'aggregation.eventZones': 'Ereigniszonen',
  'aggregation.heatmapLow': 'Niedrig',
  'aggregation.heatmapHigh': 'Hoch',
  'aggregation.selectedLapLine': 'Gewählte Runde',
  'aggregation.lapsIncluded': '{count} Runden',
  'gearshifting.wizardChooseTrack': 'Strecke wählen',
  'gearshifting.wizardSelect': 'Sessions wählen',
  'gearshifting.wizardResults': 'Ergebnisse',
  'gearshifting.analyzingLap': 'Analysiere…',

  // Aggregation Diff Analysis
  'analysis.aggregationdiff.title': 'Aggregations-Diff',
  'analysis.aggregationdiff.desc': 'Zwei Session-Aggregationen vergleichen, um Unterschiede bei Fahrlinie, Bremsen, Beschleunigung und Schalten hervorzuheben.',
  'aggregationdiff.groupA': 'Gruppe A',
  'aggregationdiff.groupB': 'Gruppe B',
  'aggregationdiff.baseline': 'Referenz',
  'aggregationdiff.comparison': 'Vergleich',
  'aggregationdiff.groupLabelPlaceholder': 'Eigenes Label (z.B. Morgen Trocken)',
  'aggregationdiff.loadSavedAggregation': 'Aus gespeicherter Aggregation laden…',
  'aggregationdiff.selectHint': 'Wähle Sessions für Gruppe A (Referenz) und Gruppe B (Vergleich), um Unterschiede bei Fahrlinie, Bremsen, Beschleunigung und Schalten sichtbar zu machen.',
  'aggregationdiff.selectHintA': 'Konfiguriere Gruppe A (Referenz), indem du eine einzelne Runde oder eine bzw. mehrere Sessions auswählst.',
  'aggregationdiff.selectHintB': 'Konfiguriere Gruppe B (Vergleich), indem du eine einzelne Runde oder eine bzw. mehrere Sessions für den Vergleich mit Gruppe A auswählst.',
  'aggregationdiff.compare': 'Aggregationen vergleichen',
  'aggregationdiff.bestLap': 'Beste Runde',
  'aggregationdiff.medianLap': 'Median-Runde',
  'aggregationdiff.consistency': 'Konstanz (σ)',
  'aggregationdiff.lapsAndSessions': '{laps} Runden · {sessions} Sessions',
  'aggregationdiff.layerDiffHeatmap': 'Linien-Diff-Heatmap',
  'aggregationdiff.layerMedianLines': 'Median-Linien',
  'aggregationdiff.layerEventDiffs': 'Ereigniszonen-Diff',
  'aggregationdiff.hideUnchanged': '< 1.5m abblenden',
  'aggregationdiff.keyDifferencesTitle': 'Wichtigste Unterschiede',
  'aggregationdiff.keyDifferencesSubtitle': 'Streckenabschnitte sortiert nach Zeitdifferenz mit möglichen Ursachen. Klicke auf einen Abschnitt zur Hervorhebung auf der Karte.',
  'aggregationdiff.filterAll': 'Alle',
  'aggregationdiff.filterLine': 'Fahrlinie',
  'aggregationdiff.filterBraking': 'Bremsen',
  'aggregationdiff.filterAcceleration': 'Beschleunigung',
  'aggregationdiff.filterUpshift': 'Hochschalten',
  'aggregationdiff.filterTopSpeed': 'Top-Speed',
  'aggregationdiff.possibleReasons': 'Mögliche Ursachen',
  'aggregationdiff.topSpeedPeak': 'Spitze {a} vs {b} km/h',
  'aggregationdiff.laterBy': '{group} {dist}m später',
  'aggregationdiff.earlierBy': '{group} {dist}m früher',
  'aggregationdiff.widerBy': 'Streuung +{dist}m breiter',
  'aggregationdiff.tighterBy': 'Streuung {dist}m enger',
  'aggregationdiff.onlyInGroup': 'Nur in {group}',
  'aggregationdiff.lineSeparation': '{dist}m max. Linienabstand ({len}m Zone)',
  'aggregationdiff.lineOffsetLeft': 'Links +{dist}m',
  'aggregationdiff.lineOffsetRight': 'Rechts +{dist}m',
  'aggregationdiff.noSignificantDiffs': 'Keine signifikanten Unterschiede für diesen Filter gefunden.',
  'aggregationdiff.sharedLine': 'Gemeinsame Linie',
  'aggregationdiff.clearFocus': 'Auswahl aufheben',
  'aggregationdiff.avgSpeed': 'Ø Speed',
  'aggregationdiff.speedDiff': 'Speed-Diff',
  'aggregationdiff.lineOffset': 'Linienabstand',
  'aggregationdiff.layerFasterSegments': 'Schnellere Segmente',
  'aggregationdiff.fasterSegmentLegend': 'Schnelleres Segment (Speed)',
  'aggregationdiff.estTimeGained': '{group} {time} schneller',
  'aggregationdiff.estTimeLost': '{group} {time} schneller',
  'aggregationdiff.estTimeNeutral': 'Geschätzt ±0.00s',

  // Analysis History
  'analysis.history.title': 'Analyse-Verlauf',
  'analysis.history.subtitle': 'Gespeicherte Telemetrie-Analysen erneut öffnen und verwalten',
  'analysis.history.filterAll': 'Alle Tools',
  'analysis.history.filterSector': 'Sektor',
  'analysis.history.filterCorner': 'Kurven',
  'analysis.history.filterBraking': 'Bremsen',
  'analysis.history.filterLapDiff': 'Rundendiff.',
  'analysis.history.filterAggregation': 'Aggregation',
  'analysis.history.filterAggregationDiff': 'Aggregations-Diff',
  'analysis.history.groupsScope': '{a} vs. {b} Session(s)',
  'analysis.history.empty': 'Keine gespeicherten Analysen gefunden',
  'analysis.history.emptyDesc': 'Analysen, die im angemeldeten Zustand ausgeführt werden, erscheinen automatisch hier.',
  'analysis.history.open': 'Öffnen',
  'analysis.history.rename': 'Umbenennen',
  'analysis.history.delete': 'Löschen',
  'analysis.history.deleteConfirm': 'Möchtest du diese Analyse wirklich aus dem Verlauf löschen?',
  'analysis.history.deleteSuccess': 'Analyse gelöscht',
  'analysis.history.renameSuccess': 'Analyse umbenannt',
  'analysis.history.searchPlaceholder': 'Analyse-Verlauf durchsuchen...',
  'analysis.history.loading': 'Analysen werden geladen...',
  'analysis.history.table.type': 'Typ',
  'analysis.history.table.name': 'Name',
  'analysis.history.table.track': 'Strecke',
  'analysis.history.table.scope': 'Umfang',
  'analysis.history.table.created': 'Erstellt',
  'analysis.history.table.actions': 'Aktionen',
  'analysis.history.total': '{n} gesamt',
  'analysis.history.sessionsCount': '{n} Session(s)',
  'analysis.history.lapsCount': '{n} Runde(n)',
  'analysis.history.viewAll': 'Vollständigen Verlauf anzeigen',
  'analysis.sessionRemoved.title': 'Session gelöscht',
  'analysis.sessionRemoved.description': 'Die in dieser Analyse verwendete Telemetrie-Session wurde gelöscht. Diese Analyse kann nicht mehr geöffnet werden.',
  'analysis.sessionRemoved.deleteAnalysis': 'Analyse löschen',
  'analysis.sessionRemoved.close': 'Schließen',

  /* ── Footer ──── */
  'footer.contact': 'Kontakt',
  'footer.privacyPolicy': 'Datenschutzerklärung',
  'footer.reportBug': 'Fehler melden',
  'footer.bugReportTitle': 'Fehler melden',
  'footer.description': 'Beschreibung',
  'footer.descriptionPlaceholder': 'Beschreibe das aufgetretene Problem...',
  'footer.submit': 'Absenden',
  'footer.submitting': 'Wird gesendet...',
  'footer.submitted': 'Fehlerbericht gesendet. Vielen Dank!',
  'footer.submitFailed': 'Fehlerbericht konnte nicht gesendet werden.',

  /* ── Error auto-reporting & Error Boundary ─────────────────────────── */
  'error.autoReported': 'Ein Fehler ist aufgetreten und wurde automatisch gemeldet.',
  'errorBoundary.title': 'Etwas ist schiefgelaufen',
  'errorBoundary.message': 'Ein unerwarteter Fehler ist aufgetreten und wurde an den Server gemeldet.',
  'errorBoundary.reload': 'Seite neu laden',
  'errorBoundary.reset': 'Erneut versuchen',
  'errorBoundary.showDetails': 'Fehlerdetails anzeigen',
  'errorBoundary.hideDetails': 'Fehlerdetails ausblenden',

  /* ── Setup Coach ──────────────────────────────────────────── */
  'coach.title': 'Setup Coach',
  'coach.subtitle': 'Eine Wissensdatenbank für Kart-Setup-Anpassungen, geordnet nach Fahrzielen. Wähle ein Problem, um empfohlene Änderungen und deren Auswirkungen zu sehen.',
  'coach.disclaimer': 'Diese Empfehlungen sind allgemeine Richtlinien für Rotax-Max-Karts. Teste Änderungen immer schrittweise und passe sie an Streckenbedingungen, Fahrer-Feedback und Reifenzustand an.',
  'coach.col.parameter': 'Parameter',
  'coach.col.change': 'Änderung',
  'coach.col.effect': 'Wirkung',
  'coach.col.sideEffects': 'Nebenwirkungen',
  'coach.col.effectiveness': 'Wirksamkeit',

  // Untersteuern reduzieren
  'coach.goal.understeer.title': 'Untersteuern reduzieren',
  'coach.goal.understeer.desc': 'Das Kart schiebt in den Kurven nach aussen und folgt nicht der gewünschten Ideallinie. Die Vorderreifen verlieren vor den Hinterreifen den Grip, wodurch das Kart nicht auf Lenkbefehle reagiert.',
  'coach.goal.understeer.adj1.param': 'Vorderachse Spurbreite',
  'coach.goal.understeer.adj1.change': 'Verbreitern',
  'coach.goal.understeer.adj1.effect': 'Mehr mechanischer Grip vorne',
  'coach.goal.understeer.adj1.side': 'Kann Vorderachse aggressiver machen',
  'coach.goal.understeer.adj2.param': 'Nachlauf (Caster)',
  'coach.goal.understeer.adj2.change': 'Erhöhen',
  'coach.goal.understeer.adj2.effect': 'Mehr Grip beim Einlenken',
  'coach.goal.understeer.adj2.side': 'Weniger Geradeausstabilität',
  'coach.goal.understeer.adj3.param': 'Sturz (Camber)',
  'coach.goal.understeer.adj3.change': 'Mehr (negativer)',
  'coach.goal.understeer.adj3.effect': 'Mehr Grip in Kurvenmitte',
  'coach.goal.understeer.adj3.side': 'Ungleichmässiger Reifenverschleiss',
  'coach.goal.understeer.adj4.param': 'Bodenfreiheit vorne',
  'coach.goal.understeer.adj4.change': 'Tiefer',
  'coach.goal.understeer.adj4.effect': 'Tieferer Schwerpunkt, mehr Grip',
  'coach.goal.understeer.adj4.side': 'Risiko des Aufsetzens',
  'coach.goal.understeer.adj5.param': 'Hinterachse',
  'coach.goal.understeer.adj5.change': 'Weicher (weniger steif)',
  'coach.goal.understeer.adj5.effect': 'Heck dreht freier ein',
  'coach.goal.understeer.adj5.side': 'Kann Heck instabil machen',
  'coach.goal.understeer.adj6.param': 'Reifendruck vorne',
  'coach.goal.understeer.adj6.change': 'Leicht erhöhen',
  'coach.goal.understeer.adj6.effect': 'Schärferes Ansprechverhalten vorne',
  'coach.goal.understeer.adj6.side': 'Weniger Aufstandsfläche',

  // Übersteuern reduzieren
  'coach.goal.oversteer.title': 'Übersteuern reduzieren',
  'coach.goal.oversteer.desc': 'Das Heck des Karts bricht in den Kurven aus und das Kart dreht zu aggressiv. Die Hinterreifen verlieren vor den Vorderreifen den Grip, was zu plötzlichem Übersteuern oder einem losen Heck führt.',
  'coach.goal.oversteer.adj1.param': 'Vorderachse Spurbreite',
  'coach.goal.oversteer.adj1.change': 'Verengen',
  'coach.goal.oversteer.adj1.effect': 'Weniger Grip vorne, ausgewogeneres Handling',
  'coach.goal.oversteer.adj1.side': 'Weniger Lenkreaktion',
  'coach.goal.oversteer.adj2.param': 'Nachlauf (Caster)',
  'coach.goal.oversteer.adj2.change': 'Reduzieren',
  'coach.goal.oversteer.adj2.effect': 'Weniger Grip vorne',
  'coach.goal.oversteer.adj2.side': 'Langsameres Einlenken',
  'coach.goal.oversteer.adj3.param': 'Hinterachse',
  'coach.goal.oversteer.adj3.change': 'Steifer',
  'coach.goal.oversteer.adj3.effect': 'Mehr Grip hinten',
  'coach.goal.oversteer.adj3.side': 'Chassis kann auf Unebenheiten springen',
  'coach.goal.oversteer.adj4.param': 'Hinterachse Breite',
  'coach.goal.oversteer.adj4.change': 'Verbreitern',
  'coach.goal.oversteer.adj4.effect': 'Mehr mechanischer Grip hinten',
  'coach.goal.oversteer.adj4.side': 'Weniger Heckrotation',
  'coach.goal.oversteer.adj5.param': 'Reifendruck hinten',
  'coach.goal.oversteer.adj5.change': 'Leicht reduzieren',
  'coach.goal.oversteer.adj5.effect': 'Mehr Aufstandsfläche hinten',
  'coach.goal.oversteer.adj5.side': 'Langsamere Reifenerwärmung',
  'coach.goal.oversteer.adj6.param': 'Stabilisator vorne',
  'coach.goal.oversteer.adj6.change': 'Einbauen',
  'coach.goal.oversteer.adj6.effect': 'Versteift Vorderachse, reduziert Grip vorne',
  'coach.goal.oversteer.adj6.side': 'Weniger Nachgiebigkeit vorne',

  // Chassis springt in Kurven
  'coach.goal.bounce.title': 'Chassis springt in Kurven',
  'coach.goal.bounce.desc': 'Das Chassis springt oder hüpft in den Kurven anstatt gleichmässigen Bodenkontakt zu halten. Dies wird typischerweise durch ein zu steifes Chassis im Verhältnis zum Gripniveau verursacht und verunmöglicht konstante Kurvengeschwindigkeit.',
  'coach.goal.bounce.adj1.param': 'Hinterachse',
  'coach.goal.bounce.adj1.change': 'Weicher (weniger steif)',
  'coach.goal.bounce.adj1.effect': 'Absorbiert Energie, glattere Fahrt',
  'coach.goal.bounce.adj1.side': 'Kann Grip hinten reduzieren',
  'coach.goal.bounce.adj2.param': 'Hinterachse Breite',
  'coach.goal.bounce.adj2.change': 'Verbreitern',
  'coach.goal.bounce.adj2.effect': 'Stabilisiert Chassis in Kurven',
  'coach.goal.bounce.adj2.side': 'Weniger Rotation am Kurvenausgang',
  'coach.goal.bounce.adj3.param': 'Heckstossstange',
  'coach.goal.bounce.adj3.change': 'Lockern',
  'coach.goal.bounce.adj3.effect': 'Mehr Flex, absorbiert Vibrationen',
  'coach.goal.bounce.adj3.side': 'Weniger starre Abstützung',
  'coach.goal.bounce.adj4.param': 'Reifendruck hinten',
  'coach.goal.bounce.adj4.change': 'Reduzieren',
  'coach.goal.bounce.adj4.effect': 'Mehr Nachgiebigkeit über Unebenheiten',
  'coach.goal.bounce.adj4.side': 'Langsameres Ansprechverhalten',
  'coach.goal.bounce.adj5.param': 'Bodenfreiheit vorne',
  'coach.goal.bounce.adj5.change': 'Erhöhen',
  'coach.goal.bounce.adj5.effect': 'Mehr Chassisfreiheit',
  'coach.goal.bounce.adj5.side': 'Weniger Grip vorne',

  // Aggressivität vorne reduzieren
  'coach.goal.frontAggressive.title': 'Aggressivität vorne reduzieren',
  'coach.goal.frontAggressive.desc': 'Die Vorderachse hat zu viel Grip, wodurch das Kart zu scharf einlenkt und das Heck destabilisiert. Das Kart fühlt sich beim Einlenken nervös an und kann plötzlich ins Übersteuern geraten.',
  'coach.goal.frontAggressive.adj1.param': 'Vorderachse Spurbreite',
  'coach.goal.frontAggressive.adj1.change': 'Verengen',
  'coach.goal.frontAggressive.adj1.effect': 'Reduziert Grip vorne',
  'coach.goal.frontAggressive.adj1.side': 'Mehr Untersteuer-Tendenz',
  'coach.goal.frontAggressive.adj2.param': 'Nachlauf (Caster)',
  'coach.goal.frontAggressive.adj2.change': 'Reduzieren',
  'coach.goal.frontAggressive.adj2.effect': 'Weniger Biss beim Einlenken',
  'coach.goal.frontAggressive.adj2.side': 'Langsameres Einlenken',
  'coach.goal.frontAggressive.adj3.param': 'Stabilisator vorne',
  'coach.goal.frontAggressive.adj3.change': 'Entfernen oder reduzieren',
  'coach.goal.frontAggressive.adj3.effect': 'Weichere Vorderachse',
  'coach.goal.frontAggressive.adj3.side': 'Weniger Präzision vorne',
  'coach.goal.frontAggressive.adj4.param': 'Sturz (Camber)',
  'coach.goal.frontAggressive.adj4.change': 'Reduzieren (weniger negativ)',
  'coach.goal.frontAggressive.adj4.effect': 'Weniger Kurvengrip',
  'coach.goal.frontAggressive.adj4.side': 'Gleichmässigerer Reifenverschleiss',
  'coach.goal.frontAggressive.adj5.param': 'Reifendruck vorne',
  'coach.goal.frontAggressive.adj5.change': 'Erhöhen',
  'coach.goal.frontAggressive.adj5.effect': 'Kleinere Aufstandsfläche',
  'coach.goal.frontAggressive.adj5.side': 'Schärfer aber weniger Grip',

  // Heckrutschen reduzieren
  'coach.goal.rearSlide.title': 'Heckrutschen reduzieren',
  'coach.goal.rearSlide.desc': 'Das Heck des Karts rutscht übermässig in Kurven, was Zeitverlust und erhöhten Reifenverschleiss verursacht. Dem Kart fehlt Hecktraktion und es fühlt sich unter Querlast instabil an.',
  'coach.goal.rearSlide.adj1.param': 'Hinterachse',
  'coach.goal.rearSlide.adj1.change': 'Steifer',
  'coach.goal.rearSlide.adj1.effect': 'Mehr Grip hinten',
  'coach.goal.rearSlide.adj1.side': 'Kann auf Unebenheiten springen',
  'coach.goal.rearSlide.adj2.param': 'Hinterachse Breite',
  'coach.goal.rearSlide.adj2.change': 'Verbreitern',
  'coach.goal.rearSlide.adj2.effect': 'Mehr mechanischer Grip hinten',
  'coach.goal.rearSlide.adj2.side': 'Weniger freie Rotation',
  'coach.goal.rearSlide.adj3.param': 'Reifendruck hinten',
  'coach.goal.rearSlide.adj3.change': 'Leicht erhöhen',
  'coach.goal.rearSlide.adj3.effect': 'Bessere Reifenform unter Last',
  'coach.goal.rearSlide.adj3.side': 'Weniger Aufstandsfläche bei Extremen',
  'coach.goal.rearSlide.adj4.param': 'Vorderachse Spurbreite',
  'coach.goal.rearSlide.adj4.change': 'Verengen',
  'coach.goal.rearSlide.adj4.effect': 'Ausgewogenere Gripverteilung',
  'coach.goal.rearSlide.adj4.side': 'Weniger Biss vorne',
  'coach.goal.rearSlide.adj5.param': 'Radnaben hinten',
  'coach.goal.rearSlide.adj5.change': 'Breiter verwenden',
  'coach.goal.rearSlide.adj5.effect': 'Mehr Hebelwirkung hinten',
  'coach.goal.rearSlide.adj5.side': 'Schwereres Lenkgefühl',

  // Traktion am Kurvenausgang
  'coach.goal.cornerExit.title': 'Traktion am Kurvenausgang verbessern',
  'coach.goal.cornerExit.desc': 'Das Kart hat Schwierigkeiten, am Kurvenausgang Leistung auf den Boden zu bringen. Die Hinterreifen drehen durch oder rutschen beim Beschleunigen aus langsamen Kurven, was Zeitverlust auf der Ausgangs-Geraden-Phase verursacht.',
  'coach.goal.cornerExit.adj1.param': 'Hinterachse Breite',
  'coach.goal.cornerExit.adj1.change': 'Verbreitern',
  'coach.goal.cornerExit.adj1.effect': 'Mehr mechanischer Grip am Ausgang',
  'coach.goal.cornerExit.adj1.side': 'Weniger Rotation in Kurvenmitte',
  'coach.goal.cornerExit.adj2.param': 'Hinterachse',
  'coach.goal.cornerExit.adj2.change': 'Steifer',
  'coach.goal.cornerExit.adj2.effect': 'Heck steht beim Beschleunigen',
  'coach.goal.cornerExit.adj2.side': 'Kann unter Last springen',
  'coach.goal.cornerExit.adj3.param': 'Reifendruck hinten',
  'coach.goal.cornerExit.adj3.change': 'Senken',
  'coach.goal.cornerExit.adj3.effect': 'Mehr Aufstandsfläche am Ausgang',
  'coach.goal.cornerExit.adj3.side': 'Langsamere Reifenerwärmung',
  'coach.goal.cornerExit.adj4.param': 'Hinteres Ritzel',
  'coach.goal.cornerExit.adj4.change': 'Grösser (mehr Zähne)',
  'coach.goal.cornerExit.adj4.effect': 'Mehr Drehmoment unten',
  'coach.goal.cornerExit.adj4.side': 'Niedrigere Höchstgeschwindigkeit',
  'coach.goal.cornerExit.adj5.param': 'Heckstossstange',
  'coach.goal.cornerExit.adj5.change': 'Lockern',
  'coach.goal.cornerExit.adj5.effect': 'Mehr Flex unter Last',
  'coach.goal.cornerExit.adj5.side': 'Weniger berechenbar',

  // Heisse Bedingungen
  'coach.goal.hotConditions.title': 'An heisse Bedingungen anpassen',
  'coach.goal.hotConditions.desc': 'Die Streckentemperatur ist hoch, was zu erhöhtem Gripniveau und Reifenüberhitzung führt. Das Chassis kann sich zu griffig anfühlen und zu Springen oder Vorderachsaggressivität führen. Der Motor läuft heisser und ein fetteres Gemisch kann nötig sein.',
  'coach.goal.hotConditions.adj1.param': 'Alle Reifendrücke',
  'coach.goal.hotConditions.adj1.change': 'Erhöhen',
  'coach.goal.hotConditions.adj1.effect': 'Kompensiert Wärmeausdehnung',
  'coach.goal.hotConditions.adj1.side': 'Kann Grip reduzieren wenn zu hoch',
  'coach.goal.hotConditions.adj2.param': 'Hauptdüse',
  'coach.goal.hotConditions.adj2.change': 'Fetter (grössere Nummer)',
  'coach.goal.hotConditions.adj2.effect': 'Verhindert Magergemisch',
  'coach.goal.hotConditions.adj2.side': 'Leichter Leistungsverlust',
  'coach.goal.hotConditions.adj3.param': 'Hinterachse',
  'coach.goal.hotConditions.adj3.change': 'Weicher',
  'coach.goal.hotConditions.adj3.effect': 'Kompensiert erhöhten Grip',
  'coach.goal.hotConditions.adj3.side': 'Kann sich lose anfühlen',
  'coach.goal.hotConditions.adj4.param': 'Hinterachse Breite',
  'coach.goal.hotConditions.adj4.change': 'Leicht verengen',
  'coach.goal.hotConditions.adj4.effect': 'Reduziert überschüssigen Grip',
  'coach.goal.hotConditions.adj4.side': 'Weniger Ausgangs-Traktion',
  'coach.goal.hotConditions.adj5.param': 'Luftschraube',
  'coach.goal.hotConditions.adj5.change': 'Öffnen (mehr Umdrehungen raus)',
  'coach.goal.hotConditions.adj5.effect': 'Fetteres Leerlaufgemisch',
  'coach.goal.hotConditions.adj5.side': 'Kann Leerlaufqualität beeinflussen',

  // Kalte/nasse Bedingungen
  'coach.goal.coldConditions.title': 'An kalte / nasse Bedingungen anpassen',
  'coach.goal.coldConditions.desc': 'Die Streckentemperatur ist niedrig oder die Oberfläche ist nass, was zu reduziertem Gripniveau führt. Das Kart braucht maximalen mechanischen Grip und die Vergasereinstellung muss dichtere Luft kompensieren. Alle Änderungen konservativ vornehmen und Fahrsicherheit priorisieren.',
  'coach.goal.coldConditions.adj1.param': 'Alle Reifendrücke',
  'coach.goal.coldConditions.adj1.change': 'Reduzieren',
  'coach.goal.coldConditions.adj1.effect': 'Kompensiert Kältekontraktion',
  'coach.goal.coldConditions.adj1.side': 'Risiko des Reifenrutschens auf der Felge',
  'coach.goal.coldConditions.adj2.param': 'Hauptdüse',
  'coach.goal.coldConditions.adj2.change': 'Magerer (kleinere Nummer)',
  'coach.goal.coldConditions.adj2.effect': 'Kompensiert dichtere Luft',
  'coach.goal.coldConditions.adj2.side': 'Risiko der Motorüberhitzung',
  'coach.goal.coldConditions.adj3.param': 'Hinterachse',
  'coach.goal.coldConditions.adj3.change': 'Steifer',
  'coach.goal.coldConditions.adj3.effect': 'Erzeugt mehr mechanischen Grip',
  'coach.goal.coldConditions.adj3.side': 'Kann auf nasser Strecke springen',
  'coach.goal.coldConditions.adj4.param': 'Spurbreite vorne & hinten',
  'coach.goal.coldConditions.adj4.change': 'Beide verbreitern',
  'coach.goal.coldConditions.adj4.effect': 'Maximiert mechanischen Grip',
  'coach.goal.coldConditions.adj4.side': 'Aggressiveres Handling',
  'coach.goal.coldConditions.adj5.param': 'Luftschraube',
  'coach.goal.coldConditions.adj5.change': 'Schliessen (weniger Umdrehungen raus)',
  'coach.goal.coldConditions.adj5.effect': 'Magereres Leerlaufgemisch',
  'coach.goal.coldConditions.adj5.side': 'Kann Kaltstart beeinflussen',

  // Bremsstabilität
  'coach.goal.braking.title': 'Bremsstabilität verbessern',
  'coach.goal.braking.desc': 'Das Kart ist beim Bremsen instabil, das Heck bricht aus oder die Vorderachse blockiert. Dies erschwert spätes und konstantes Bremsen und kostet Zeit beim Kurveneingang.',
  'coach.goal.braking.adj1.param': 'Reifendruck vorne',
  'coach.goal.braking.adj1.change': 'Erhöhen',
  'coach.goal.braking.adj1.effect': 'Präzisere Vorderachse beim Bremsen',
  'coach.goal.braking.adj1.side': 'Weniger Brems-Aufstandsfläche',
  'coach.goal.braking.adj2.param': 'Nachlauf (Caster)',
  'coach.goal.braking.adj2.change': 'Erhöhen',
  'coach.goal.braking.adj2.effect': 'Mehr Nachlauf, stabilisiert beim Bremsen',
  'coach.goal.braking.adj2.side': 'Schwerere Lenkung',
  'coach.goal.braking.adj3.param': 'Bodenfreiheit vorne',
  'coach.goal.braking.adj3.change': 'Tiefer',
  'coach.goal.braking.adj3.effect': 'Mehr Gewichtsverlagerung nach vorne',
  'coach.goal.braking.adj3.side': 'Risiko des Aufsetzens',
  'coach.goal.braking.adj4.param': 'Stabilisator vorne',
  'coach.goal.braking.adj4.change': 'Einbauen',
  'coach.goal.braking.adj4.effect': 'Versteift Vorderachse beim Bremsen',
  'coach.goal.braking.adj4.side': 'Kann Einlenk-Grip reduzieren',
  'coach.goal.braking.adj5.param': 'Reifendruck hinten',
  'coach.goal.braking.adj5.change': 'Reduzieren',
  'coach.goal.braking.adj5.effect': 'Verhindert Hinterradblockierung',
  'coach.goal.braking.adj5.side': 'Langsameres Ansprechverhalten hinten',

  // Motor-Feintuning
  'coach.goal.engine.title': 'Motor-Performance optimieren',
  'coach.goal.engine.desc': 'Optimiere die Motorleistung durch Getriebe- und Vergasereinstellungen. Diese Änderungen beeinflussen Leistungsentfaltung, Beschleunigung, Höchstgeschwindigkeit und Motorsicherheit. Immer die AGT (Abgastemperatur) überwachen bei Düsenänderungen.',
  'coach.goal.engine.adj1.param': 'Hinteres Ritzel',
  'coach.goal.engine.adj1.change': 'Grösser (mehr Zähne)',
  'coach.goal.engine.adj1.effect': 'Mehr Beschleunigung, tiefere Endgeschwindigkeit',
  'coach.goal.engine.adj1.side': 'Höhere Drehzahl auf Geraden',
  'coach.goal.engine.adj2.param': 'Hinteres Ritzel',
  'coach.goal.engine.adj2.change': 'Kleiner (weniger Zähne)',
  'coach.goal.engine.adj2.effect': 'Höhere Endgeschwindigkeit, weniger Beschleunigung',
  'coach.goal.engine.adj2.side': 'Niedrigere Drehzahl, langsamere Ausgänge',
  'coach.goal.engine.adj3.param': 'Hauptdüse',
  'coach.goal.engine.adj3.change': 'Fetter (grössere Nummer)',
  'coach.goal.engine.adj3.effect': 'Kühlere AGT, sicherer',
  'coach.goal.engine.adj3.side': 'Leichter Leistungsverlust',
  'coach.goal.engine.adj4.param': 'Hauptdüse',
  'coach.goal.engine.adj4.change': 'Magerer (kleinere Nummer)',
  'coach.goal.engine.adj4.effect': 'Mehr Leistung',
  'coach.goal.engine.adj4.side': 'Risiko eines Kolbenklemmers, höhere AGT',
  'coach.goal.engine.adj5.param': 'Nadelclip',
  'coach.goal.engine.adj5.change': 'Position anheben',
  'coach.goal.engine.adj5.effect': 'Magererer Mittelbereich, mehr Leistung',
  'coach.goal.engine.adj5.side': 'Risiko eines Magergemischs',
  'coach.goal.engine.adj6.param': 'Schwimmerhöhe',
  'coach.goal.engine.adj6.change': 'Anpassen',
  'coach.goal.engine.adj6.effect': 'Steuert Kraftstoffniveau in der Schwimmerkammer',
  'coach.goal.engine.adj6.side': 'Beeinflusst alle Drehzahlbereiche',

  // Track Map Controls
  'trackMap.zoomIn': 'Vergrössern',
  'trackMap.zoomOut': 'Verkleinern',
  'trackMap.fitTrack': 'Ganze Strecke',
  'trackMap.satellite': 'Satellit',
  'trackMap.vector': 'Vektor',
  'trackMap.switchProvider': 'Luftbild-Anbieter wechseln',

  // Lap Analysis Queue & Amended Analysis
  'queue.processingLaps': 'Rundenanalyse {done}/{total}',
  'queue.completed': 'Rundenanalyse abgeschlossen ({total}/{total})',
  'tracks.reanalyzeLaps': 'Runden neu analysieren',
  'tracks.reanalyzingLaps': 'Wird eingereiht…',
  'tracks.reanalyzeQueued': '{enqueued} Runde(n) zur Analyse eingereiht ({skipped} bereits aktuell).',
  'tracks.reanalyzeAllUpToDate': 'Alle {skipped} Runde(n) sind bereits auf dem neuesten Streckenstand.',
  'lapInfo.canonicalSectors': 'Streckensektoren',
  'lapInfo.loggerSectors': 'Logger-Sektoren',
  'lapAnalysis.amendedCornersTitle': 'Kurven- & Bremsanalyse',
  'lapAnalysis.cornerCol': 'Kurve',
  'lapAnalysis.topApproachSpeed': 'Top-Speed vor Bremsen',
  'lapAnalysis.slowestSpeed': 'Langsamste Geschw.',
  'lapAnalysis.apexSpeed': 'Scheitel-Geschw.',
  'lapAnalysis.exitSpeed': 'Ausgangs-Geschw.',
  'lapAnalysis.brakingDist': 'Bremsweg',
  'lapAnalysis.cornerTime': 'Dauer',
  'lapAnalysis.statusCompleted': 'Analyse abgeschlossen',
  'lapAnalysis.statusPending': 'Analyse ausstehend',
  'session.tabCorners': 'Kurven',
  'session.noCornersConfigured': 'Keine Kurvenanalyse-Daten für diese Session verfügbar.',
  'lapAnalysis.entrySpeed': 'Eingangs-Geschw.',
  'lapAnalysis.trailBraking': 'Trail-Braking',

  // Terms & Conditions
  'footer.termsAndConditions': 'Allgemeine Geschäftsbedingungen',
  'terms.gateTitle': 'Allgemeine Geschäftsbedingungen',
  'terms.gateSubtitle': 'Bitte lies und akzeptiere die aktuellen Nutzungsbedingungen, um fortzufahren.',
  'terms.updatedBannerTitle': 'Aktualisierte Nutzungsbedingungen:',
  'terms.updatedBannerDesc': 'Unsere Nutzungsbedingungen wurden aktualisiert. Bitte lies und akzeptiere die neue Version, um kart.win weiter zu nutzen.',
  'terms.checkboxLabel': 'Ich habe die Allgemeinen Geschäftsbedingungen gelesen und stimme ihnen zu',
  'terms.acceptButton': 'Akzeptieren & Weiter',
  'terms.acceptingButton': 'Wird gespeichert…',
  'terms.loadError': 'Die Nutzungsbedingungen konnten nicht geladen werden. Bitte überprüfe deine Verbindung und versuche es erneut.',
  'terms.acceptError': 'Deine Zustimmung konnte nicht gespeichert werden. Bitte versuche es erneut.',

  // Additional Common & Import Keys
  'common.previous': 'Zurück',
  'common.remove': 'Entfernen',
  'common.success': 'Erfolgreich gespeichert',
  'common.failedToSave': 'Speichern fehlgeschlagen',
  'import.useCurrentSetupDesc': 'Aktuelles Kart-Setup automatisch mit den importierten Sessions verknüpfen.',

  // Search & Filter Bar
  'search.booleanLogicUse': 'Verbinde Begriffe mit',
  'search.booleanLogicOr': 'oder',
  'search.booleanLogicGroup': 'und gruppiere Ausdrücke mit Klammern',
  'filter.operator.eq': 'ist',
  'filter.operator.neq': 'ist nicht',
  'filter.operator.gt': 'größer als',
  'filter.operator.gte': 'mindestens',
  'filter.operator.lt': 'kleiner als',
  'filter.operator.lte': 'höchstens',
  'filter.operator.range': 'zwischen',
  'filter.operator.contains': 'enthält',
  'filter.remove': 'Filter entfernen',
  'filter.done': 'Fertig',
  'filter.clearInput': 'Eingabe löschen',
  'filter.selectField': 'Nach Feld filtern',
  'filter.pressEnter': 'Eingabetaste zum Anwenden drücken',
  'filter.selectValues': 'Werte auswählen',
  'filter.noMatchingOptions': 'Keine passenden Optionen',

  // Search Schemas
  'filter.session.bareSearchExample': 'Nach Streckenname suchen',
  'filter.session.searchTrack': 'Nach Streckenname filtern',
  'filter.session.filterLaps': 'Nach Rundenanzahl filtern',
  'filter.session.filterBestTime': 'Nach bester Rundenzeit filtern (Sekunden)',
  'filter.session.filterMaxSpeed': 'Nach Höchstgeschwindigkeit filtern (km/h)',
  'filter.session.filterMaxRpm': 'Nach maximaler Drehzahl filtern',
  'filter.session.filterDate': 'Nach Session-Datum filtern',
  'filter.session.filterShared': 'Nach Team-Freigabestatus filtern',
  'filter.session.filterVisibility': 'Nach Besitz oder Team-Sichtbarkeit filtern',
  'filter.session.visibilityMine': 'Meine Sessions',
  'filter.session.visibilityShared': 'Mit Team geteilt',
  'filter.session.exampleTrackLaps': 'Sessions in Lonato mit 10+ Runden',
  'filter.session.exampleBestSpeed': 'Schnelle Runden mit hohem Top-Speed',

  'filter.analysis.bareSearchExample': 'Nach Analysetitel oder Strecke suchen',
  'filter.analysis.searchTitle': 'Nach Analysetitel filtern',
  'filter.analysis.searchTrack': 'Nach Streckenname filtern',
  'filter.analysis.filterType': 'Nach Analysetyp filtern',
  'filter.analysis.filterDate': 'Nach Erstellungsdatum filtern',
  'filter.analysis.filterShared': 'Nach Team-Freigabestatus filtern',
  'filter.analysis.filterVisibility': 'Nach Besitz oder Team-Freigabe filtern',
  'filter.analysis.exampleCornerTrack': 'Kurvenanalysen in Lonato',
  'filter.analysis.exampleSharedDate': 'Geteilte Analysen aus 2026',

  'filter.trackRaces.bareSearchExample': 'Nach Eventname, Saison oder Kategorie suchen',
  'filter.trackRaces.searchTitle': 'Nach Eventname filtern',
  'filter.trackRaces.searchSeason': 'Nach Meisterschaftssaison filtern',
  'filter.trackRaces.searchCategory': 'Nach Rennkategorie filtern',
  'filter.trackRaces.filterRound': 'Nach Rundennummer filtern',
  'filter.trackRaces.filterDate': 'Nach Eventdatum filtern',
  'filter.trackRaces.exampleCatDate': 'ROK Mini Events im Jahr 2026',
  'filter.trackRaces.exampleSeasonRound': 'Erste zwei Runden des ROK Cup',

  'filter.tracks.bareSearchExample': 'Nach Streckenname oder Land suchen',
  'filter.tracks.searchName': 'Nach Streckenname filtern',
  'filter.tracks.searchCountry': 'Nach Ländercode filtern',
  'filter.tracks.filterType': 'Nach Streckentyp filtern (offiziell oder gemessen)',
  'filter.tracks.filterSessions': 'Nach Anzahl erfasster Sessions filtern',
  'filter.tracks.filterLaps': 'Nach Anzahl erfasster Runden filtern',
  'filter.tracks.filterEvents': 'Nach Anzahl offizieller Renn-Events filtern',
  'filter.tracks.filterDrivers': 'Nach Anzahl offizieller Fahrer filtern',
  'filter.tracks.exampleItSessions': 'Italienische Strecken mit erfassten Sessions',
  'filter.tracks.exampleOfficialEvents': 'Offizielle Strecken mit 3+ Renn-Events',

  'filter.leaderboard.bareSearchExample': 'Nach Fahrer, Kategorie, Saison oder Event suchen',
  'filter.leaderboard.searchDriver': 'Nach Fahrername filtern',
  'filter.leaderboard.searchCategory': 'Nach Rennkategorie filtern',
  'filter.leaderboard.searchSeason': 'Nach Meisterschaftssaison filtern',
  'filter.leaderboard.searchEvent': 'Nach Eventname filtern',
  'filter.leaderboard.filterLapTime': 'Nach Rundenzeit filtern (Sekunden)',
  'filter.leaderboard.filterSpeed': 'Nach Höchstgeschwindigkeit filtern (km/h)',
  'filter.leaderboard.filterRpm': 'Nach maximaler Drehzahl filtern',
  'filter.leaderboard.filterDate': 'Nach Rundendatum filtern',
  'filter.leaderboard.exampleTimeSpeed': 'Runden unter 48s mit hohem Top-Speed',
  'filter.leaderboard.exampleCatTime': 'Mini-Runden zwischen 52s und 54s',

  'filter.tyres.bareSearchExample': 'Nach Reifenname, Marke, Modell oder Barcode suchen',
  'filter.tyres.searchName': 'Nach Reifensatz-Name filtern',
  'filter.tyres.searchBrand': 'Nach Reifenmarke filtern',
  'filter.tyres.searchModel': 'Nach Reifenmischung/-modell filtern',
  'filter.tyres.searchSize': 'Nach Reifengröße filtern',
  'filter.tyres.searchBarcode': 'Nach gescanntem Barcode filtern',
  'filter.tyres.filterStatus': 'Nach Lebenszyklus-Status filtern',
  'filter.tyres.filterCondition': 'Nach verbleibendem Zustand filtern (%)',
  'filter.tyres.filterLaps': 'Nach gefahrenen Gesamtrunden filtern',
  'filter.tyres.filterDistance': 'Nach gefahrener Gesamtdistanz filtern (km)',
  'filter.tyres.exampleActiveBrand': 'Aktive LeCont-Reifensätze',
  'filter.tyres.exampleConditionLaps': 'Reifen mit >50% Zustand und <30 Runden',

  'filter.teamMembers.bareSearchExample': 'Nach Name oder E-Mail suchen',
  'filter.teamMembers.searchName': 'Nach Mitgliedername filtern',
  'filter.teamMembers.searchEmail': 'Nach E-Mail-Adresse filtern',
  'filter.teamMembers.filterRole': 'Nach Team-Rolle filtern',
  'filter.teamMembers.exampleRoleAdmin': 'Team-Administratoren',
  'filter.teamMembers.exampleNameRole': 'Mitglieder oder Editoren nach Name',

  'filter.teamSharing.bareSearchExample': 'Nach Titel, Besitzer oder Strecke suchen',
  'filter.teamSharing.searchTitle': 'Nach Elementtitel filtern',
  'filter.teamSharing.searchOwner': 'Nach Besitzername filtern',
  'filter.teamSharing.searchTrack': 'Nach Streckenname filtern',
  'filter.teamSharing.filterKind': 'Nach geteiltem Elementtyp filtern',
  'filter.teamSharing.kindSession': 'Session',
  'filter.teamSharing.kindAnalysis': 'Analyse',
  'filter.teamSharing.filterAccess': 'Nach Zugriffsberechtigung filtern',
  'filter.teamSharing.accessRead': 'Nur Lesen',
  'filter.teamSharing.accessWrite': 'Lesen & Schreiben',
  'filter.teamSharing.exampleSessionTrack': 'Geteilte Sessions aus Lonato',
  'filter.teamSharing.exampleAnalysisWrite': 'Bearbeitbare geteilte Analysen',

  'filter.drivers.bareSearchExample': 'Nach Fahrername, Team, Kategorie oder Material suchen',
  'filter.drivers.searchName': 'Nach Fahrername filtern',
  'filter.drivers.searchCountry': 'Nach Nationalitätscode filtern',
  'filter.drivers.searchTeam': 'Nach Rennteam filtern',
  'filter.drivers.searchCategory': 'Nach Rennkategorie filtern',
  'filter.drivers.searchChassis': 'Nach Chassismarke filtern',
  'filter.drivers.searchEngine': 'Nach Motorenmarke filtern',
  'filter.drivers.filterEntries': 'Nach Anzahl Rennteilnahmen filtern',
  'filter.drivers.exampleCountryEntries': 'Italienische Fahrer mit 5+ Teilnahmen',
  'filter.drivers.exampleCatTeam': 'Mini-Fahrer eines bestimmten Teams',

  'filter.driverHistory.bareSearchExample': 'Nach Saison, Event, Strecke oder Kategorie suchen',
  'filter.driverHistory.searchSeason': 'Nach Meisterschaftssaison filtern',
  'filter.driverHistory.searchEvent': 'Nach Eventname filtern',
  'filter.driverHistory.searchTrack': 'Nach Streckenname filtern',
  'filter.driverHistory.searchCategory': 'Nach Kategorie filtern',
  'filter.driverHistory.searchTeam': 'Nach Teamname filtern',
  'filter.driverHistory.filterPos': 'Nach Endplatzierung filtern',
  'filter.driverHistory.filterGrid': 'Nach Startplatz filtern',
  'filter.driverHistory.filterPoints': 'Nach erzielten Meisterschaftspunkten filtern',
  'filter.driverHistory.filterBestLap': 'Nach schnellster Rundenzeit filtern (Sekunden)',
  'filter.driverHistory.filterPenalties': 'Nach Anzahl Strafen filtern',
  'filter.driverHistory.examplePodiums': 'Podestplätze (Top 3)',
  'filter.driverHistory.examplePointsPenalty': 'Punktereiche Events ohne Strafen',

  // Additional Analysis & Component Keys
  'braking.colSelect': 'Ausw',
  'braking.colCompare': 'Vgl',
  'braking.scoreUnit': '/ 100',
  'braking.trackOverview': 'Streckenübersicht',
  'braking.cornersCount': '{count} Kurven',
  'braking.trackAnalysis': 'Strecken-Bremsanalyse',
  'braking.lapsTotal': '{count} Runden gesamt',
  'braking.sample': 'Messpunkt',
  'lapdiff.pointDetail': 'Punktdetails',
  'lapdiff.selectTwoLaps': '2 Runden auswählen',
  'lapdiff.lapTime': 'Rundenzeit',
  'aggregationdiff.toggleLayers': 'Kartenebenen umschalten',
  'aggregationdiff.leftInner': 'Links (Innen)',
  'aggregationdiff.rightOuter': 'Rechts (Außen)',
  'aggregationdiff.selectLap': 'Runde auswählen',
  'aggregationdiff.modeSingleLap': 'Einzelne Runde',
  'aggregationdiff.modeSessions': 'Sessions',
  'braking.phaseBraking': 'Bremsen',
  'braking.phaseAccel': 'Beschleunigung',
  'setup.tabFront': 'Vorne',
  'setup.tabRear': 'Hinten',
  'team.you': 'Du',
  'races.entries': 'Teilnehmer',
  // Filter Schema & UI Translations
  'filter.filterBy': 'Nach {label} filtern',
  'filter.removeFilter': 'Filter {label} entfernen',
  'filter.prevMonth': 'Vorheriger Monat',
  'filter.nextMonth': 'Nächster Monat',
  'filter.selectStartDate': 'Startdatum im Kalender auswählen',
  'filter.op.exact': 'Genau',
  'filter.op.atLeast': 'Mindestens',
  'filter.op.atMost': 'Höchstens',
  'filter.op.between': 'Zwischen',
  'filter.op.greater': 'Größer',
  'filter.op.less': 'Kleiner',
  'filter.preset.today': 'Heute',
  'filter.preset.last7d': 'Letzte 7 Tage',
  'filter.preset.last30d': 'Letzte 30 Tage',
  'filter.preset.last90d': 'Letzte 90 Tage',
  'filter.preset.last1y': 'Letztes Jahr',
  'filter.preset.last7dShort': 'Letzte 7T',
  'filter.preset.last30dShort': 'Letzte 30T',
  'filter.preset.last90dShort': 'Letzte 90T',
  'filter.preset.last1yShort': 'Letztes 1J',
  'filter.sessionType.race': 'Rennen',
  'filter.sessionType.final': 'Finale',
  'filter.sessionType.pre_final': 'Prefinale',
  'filter.sessionType.heat': 'Vorlauf',
  'filter.sessionType.qualifying': 'Zeitfahren',
  'filter.unit.laps': 'Runden',
  'filter.schema.tyres.title': 'Reifen-Suchsyntax',
  'filter.schema.tyres.subtitle': 'Reifen nach Marke, Mischung, Nutzung, Barcode, Strecke oder Entsorgungsstatus filtern.',
  'filter.field.brand': 'Marke',
  'filter.desc.brand': 'Reifenhersteller (Mojo, Vega, LeCont, Bridgestone usw.)',
  'filter.field.tyreModel': 'Reifenmodell',
  'filter.placeholder.tyreModel': 'z. B. D2, D5, W5, CIK Option',
  'filter.desc.tyreModel': 'Modellname oder Mischungsspezifikation',
  'filter.field.compound': 'Mischung',
  'filter.desc.compound': 'Reifenmischungskategorie (Trocken, Nass, Slick, Regen)',
  'filter.compound.dry': 'Trocken',
  'filter.compound.wet': 'Nass',
  'filter.compound.slick': 'Slick',
  'filter.compound.rain': 'Regen',
  'filter.field.setNumber': 'Satznummer',
  'filter.desc.setNumber': 'Im Inventar vergebene Reifensatznummer',
  'filter.field.totalLaps': 'Gesamtrunden',
  'filter.desc.totalLaps': 'Kumulierte Gesamtrunden auf diesem Reifensatz',
  'filter.preset.tyreFresh': 'Frisch (0..15 Runden)',
  'filter.preset.tyreBrokenIn': 'Angefahren (16..50 Runden)',
  'filter.preset.tyreHighMileage': 'Hohe Laufleistung (> 50 Runden)',
  'filter.field.barcode': 'Barcode',
  'filter.placeholder.barcode': 'VL-, VR-, HL-, HR-Barcode',
  'filter.desc.barcode': 'Teilübereinstimmung mit einem Reifen-Barcode des Satzes',
  'filter.field.lastTrack': 'Letzte Strecke',
  'filter.desc.lastTrack': 'Letzte Strecke, auf der dieser Reifensatz verwendet wurde',
  'filter.field.discarded': 'Entsorgt',
  'filter.desc.discarded': 'Ob der Reifensatz als entsorgt markiert wurde',
  'filter.discarded.false': 'Nein',
  'filter.discarded.true': 'Ja',
  'filter.example.activeTyres': 'Aktive (nicht entsorgte) Reifensätze',
  'filter.example.dryMojo': 'Trockenreifen von Mojo',
  'filter.example.highMileage70': 'Reifensätze mit hoher Laufleistung über 70 Runden',
  'filter.example.freshTyres': 'Frische Reifen mit geringer Laufleistung',
  'filter.example.lonatoTyres': 'Reifen, die seit August 2026 in Lonato genutzt wurden',
  'filter.example.vegaOrWet': 'Vega-Sätze oder beliebige Regenreifen',
  'filter.field.date': 'Datum',
  'filter.desc.date': 'Nach relativem Zeitraum (-7d, -30d, today) oder Kalenderdatum filtern',
  'filter.field.track': 'Strecke',
  'filter.desc.track': 'Stimmt mit dem Streckennamen überein',
  'filter.placeholder.track': 'z. B. Wohlen, Lonato',
  'filter.field.driver': 'Fahrer',
  'filter.desc.driver': 'Stimmt mit dem Fahrernamen überein',
  'filter.placeholder.driver': 'z. B. Yanick',
  'filter.field.season': 'Saison',
  'filter.desc.season': 'Stimmt mit dem Saisonjahr überein',
  'filter.field.category': 'Kategorie',
  'filter.desc.category': 'Kanonische Rennkategorie',
  'filter.field.championship': 'Meisterschaft',
  'filter.desc.championship': 'Name der Meisterschaftsserie',
  'filter.field.sessionType': 'Sessiontyp',
  'filter.desc.sessionType': 'Sessionklassifizierung (Rennen, Vorlauf, Zeitfahren, Finale)',
  'filter.field.position': 'Position',
  'filter.field.finishPosition': 'Endplatzierung',
  'filter.desc.position': 'Endplatzierung (z. B. position:<=3 für Podestplätze)',
  'filter.preset.posWinner': 'Sieger (P1)',
  'filter.preset.posPodium': 'Podest (≤ P3)',
  'filter.preset.posTop5': 'Top 5 (≤ P5)',
  'filter.preset.posTop10': 'Top 10 (≤ P10)',
  'filter.field.temp': 'Temperatur',
  'filter.desc.temp': 'Umgebungstemperatur der Session in Celsius (z. B. temp:15..25)',
  'filter.preset.tempCool': 'Kühl (< 15°C)',
  'filter.preset.tempMild': 'Mild (15..25°C)',
  'filter.preset.tempWarm': 'Warm (≥ 25°C)',
  'filter.field.laps': 'Runden',
  'filter.desc.laps': 'Stimmt mit der Anzahl gefahrener Runden überein (z. B. laps:>=5, laps:5..20)',
  'filter.preset.lapsShort': 'Kurz (≤ 5 Runden)',
  'filter.preset.lapsMedium': 'Mittel (6..15 Runden)',
  'filter.preset.lapsLong': 'Lang (> 15 Runden)',
  'analysis.sector': 'Sektoranalyse',
  'analysis.corner': 'Kurvengeschwindigkeit',
  'analysis.braking': 'Bremsanalyse',
  'analysis.lapdiff': 'Rundenvergleich',
  'analysis.aggregation': 'Aggregation',
  'analysis.aggregationdiff': 'Aggregationsvergleich',
  'filter.schema.analyses.title': 'Analyse-Suchsyntax',
  'filter.schema.analyses.subtitle': 'Analyseverlauf mit strukturierten Schlüsseln oder Stichwörtern filtern.',
  'filter.field.nameTrack': 'Name / Strecke',
  'filter.placeholder.nameTrack': 'z. B. Sarno, Eigener Lauf',
  'filter.desc.nameTrack': 'Stimmt mit Analysename oder Streckenname überein (Teilübereinstimmung)',
  'filter.field.toolType': 'Analysetyp',
  'filter.placeholder.toolType': 'z. B. sector, corner, lapdiff, aggregation, aggregationdiff',
  'filter.desc.toolType': 'Nach Analysewerkzeug filtern (sector, corner, lapdiff, aggregation, aggregationdiff)',
  'filter.example.sarnoSector': 'Sektoranalysen in Sarno',
  'filter.example.lapdiff30d': 'Rundenvergleiche der letzten 30 Tage',
  'filter.example.wohlenCorner': 'Kurvenanalysen in Wohlen oder Mirecourt',
  'filter.schema.sessions.title': 'Session-Suchsyntax',
  'filter.schema.sessions.subtitle': 'Sessions mit strukturierten Schlüsseln oder Freitext-Stichwörtern filtern.',
  'filter.field.trackDriver': 'Strecke / Fahrer',
  'filter.placeholder.trackDriver': 'z. B. Wohlen, Mirecourt',
  'filter.desc.trackDriver': 'Stimmt mit Streckenname oder Fahrername überein (Teilübereinstimmung)',
  'filter.schema.driverHistory.title': 'Fahrerhistorie-Suchsyntax',
  'filter.schema.driverHistory.subtitle': 'Karriere-Rennhistorie nach Strecke, Meisterschaft, Kategorie, Sessiontyp, Position oder Saison filtern.',
  'filter.example.podiumWohlen': 'Podestplätze in Wohlen',
  'filter.example.dd2Races2026': 'DD2-Rennen 2026',
  'filter.example.victoriesLast1y': 'Rennsiege im letzten Jahr',
  'filter.schema.driversList.title': 'Fahrer-Suchsyntax',
  'filter.schema.driversList.subtitle': 'Fahrer nach Name, aktiver Saison, Kategorie oder Teilnahmedatum filtern.',
  'filter.field.driverName': 'Fahrername',
  'filter.placeholder.driverName': 'z. B. Yanick, Senn',
  'filter.desc.driverName': 'Stimmt mit Vor- oder Nachnamen des Fahrers überein',
  'filter.example.findDriverYanick': 'Fahrer mit dem Namen Yanick finden',
  'filter.example.activeDd22026': 'Aktive DD2-Fahrer 2026',
  'filter.example.driversLast90d': 'Fahrer mit Rennen in den letzten 90 Tagen',
  'filter.schema.teamMembers.title': 'Teammitglieder-Suchsyntax',
  'filter.schema.teamMembers.subtitle': 'Teammitglieder nach Name, Rolle oder E-Mail filtern.',
  'filter.field.memberName': 'Mitgliedsname',
  'filter.placeholder.memberName': 'z. B. Yanick, Alice',
  'filter.desc.memberName': 'Stimmt mit dem Anzeigenamen des Mitglieds überein',
  'filter.field.role': 'Rolle',
  'filter.desc.role': 'Berechtigungsstufe (Admin, Verwalten, Lesen)',
  'team.role.admin': 'Admin',
  'filter.field.email': 'E-Mail',
  'filter.placeholder.email': 'z. B. gmail.com',
  'filter.desc.email': 'Stimmt mit der E-Mail-Adresse überein',
  'filter.example.findMemberYanick': 'Mitglieder mit dem Namen Yanick finden',
  'filter.example.findTeamAdmins': 'Team-Administratoren finden',
  'filter.example.findTeamManage': 'Mitglieder mit Verwaltungsrechten finden',
  'filter.schema.teamSharing.title': 'Team-Freigaben-Suchsyntax',
  'filter.schema.teamSharing.subtitle': 'Geteilte Zugriffe nach Eigentümer, Freigabetyp oder Berechtigungsstufe filtern.',
  'filter.field.ownerName': 'Eigentümer / Name',
  'filter.desc.ownerName': 'Stimmt mit dem Namen des Eigentümers überein',
  'filter.field.shareScope': 'Freigabeumfang',
  'filter.desc.shareScope': 'Art des Freigabeumfangs (alle Daten, einzelne Session, bestimmte Strecke)',
  'filter.shareScope.all': 'Gesamte Telemetrie',
  'filter.shareScope.single': 'Einzelne Session',
  'filter.shareScope.track': 'Bestimmte Strecke',
  'filter.example.sharesYanick': 'Freigaben von Yanick finden',
  'filter.example.sharesAllRead': 'Schreibgeschützte Voll-Telemetriefreigaben',
  'filter.schema.trackLeaderboard.title': 'Strecken-Bestenliste-Suchsyntax',
  'filter.schema.trackLeaderboard.subtitle': 'Runden und Sessions der Bestenliste nach Fahrer, Saison, Kategorie, Position oder Temperatur filtern.',
  'filter.example.yanickFastest2026': 'Yanicks schnellste Runden in der Saison 2026',
  'filter.example.podiumSeniorMax': 'Podestplätze in Senior MAX',
  'filter.example.warm90d': 'Sessions bei warmem Wetter in den letzten 90 Tagen',
  'filter.schema.trackRaceEvents.title': 'Rennevents-Suchsyntax',
  'filter.schema.trackRaceEvents.subtitle': 'Rennevents nach Saison, Meisterschaft, Kategorie oder teilnehmendem Fahrer filtern.',
  'filter.desc.driverEvent': 'Stimmt mit einem am Event teilnehmenden Fahrer überein',
  'filter.example.rmc2026': 'RMC-Switzerland-Events 2026',
  'filter.example.yanick2025': 'Events mit Teilnahme von Yanick im Jahr 2025',
  'filter.example.dd2Last1y': 'DD2-Events im letzten Jahr',
  'filter.schema.tracks.title': 'Strecken-Suchsyntax',
  'filter.schema.tracks.subtitle': 'Strecken nach Name oder Stichwörtern filtern.',
  'filter.field.trackName': 'Streckenname',
  'filter.example.findWohlen': 'Strecken passend zu Wohlen finden',
  'filter.example.exactTrack': 'Exakte Übereinstimmung des Streckennamens',
};
